
/**
 * gemini.ts — Frontend AI orchestration layer.
 *
 * Transport: all Gemini calls go through /api/generate (server-side).
 * The @google/genai SDK is NOT statically imported here.
 * The API key is NEVER present in the frontend bundle.
 *
 * Feature flag: VITE_USE_SERVER_GEMINI
 *   Omitted / 'true'  → server transport (production default).
 *   'false'           → development-only direct SDK path (requires local key).
 *
 * All prompts, schemas, and exported function signatures are unchanged.
 *
 * Schema note: Type and ThinkingLevel are pure string constant objects.
 * They are redefined inline here so the @google/genai SDK has ZERO static
 * imports, which eliminates the Rollup static+dynamic import warning and
 * ensures the SDK never appears in the production bundle's static import graph.
 * The server reads these field names as plain strings — the values are identical.
 */

// ── Inline schema type constants (mirrors @google/genai exactly) ──────────────
// These are plain string enums — no API client code, no key access.
const Type = {
  STRING:  'string',
  NUMBER:  'number',
  BOOLEAN: 'boolean',
  OBJECT:  'object',
  ARRAY:   'array',
} as const;

export const getFilteredLedger = (ledger: any, activeCanonId: string, activeProjectId: string): any => {
  const isAllowed = (item: any) => {
    if (!item || typeof item !== 'object') return true;
    if (item.canonId) {
      return item.canonId === activeCanonId;
    }
    if (item.projectId) {
      return item.projectId === activeProjectId;
    }
    return true; // legacy item that hasn't been migrated
  };

  const filterArray = (arr: any[] | undefined) => {
    if (!arr) return [];
    return arr.filter(isAllowed);
  };

  const filterObj = (obj: any) => {
    if (!obj) return {};
    const res: any = {};
    for (const key of Object.keys(obj)) {
      if (isAllowed(obj[key])) {
        res[key] = obj[key];
      }
    }
    return res;
  };

  return {
    ...ledger,
    inventory: filterArray(ledger.inventory),
    plot_points: filterArray(ledger.plot_points),
    resolved_plot_points: filterArray(ledger.resolved_plot_points),
    historical_events: filterArray(ledger.historical_events),
    continuity_objects: filterArray(ledger.continuity_objects),
    physical_continuity_obligations: filterArray(ledger.physical_continuity_obligations),
    characters: filterObj(ledger.characters),
    locations: filterObj(ledger.locations)
  };
};

const ThinkingLevel = {
  HIGH:   'HIGH',
  MEDIUM: 'MEDIUM',
  LOW:    'LOW',
  NONE:   'NONE',
} as const;

import { NarrativeStateDelta, BookArcContract, ChapterProgressionContract } from "../src/types";
import { Ledger, ValidationResult, Chapter, StorySeed, EditorResult, StoryBible, StoryMode, ChapterLength, ChapterPlan, PublicationEditorResult, SoftStateBrief } from "../src/types";
import { GEMINI_MODEL_PRO, GEMINI_MODEL_FLASH, SYSTEM_INSTRUCTIONS } from "../src/constants";
import {
  buildExpansionMomentumGuard,
  buildInspectorMomentumAddendum,
  buildNarrativeMotionContractsFromPlan,
  buildNarrativeMotionPrompt,
  buildProseControlPrompt,
  buildWordCountPolicyPrompt,
  evaluateChapterMomentum,
  formatMomentumCorrectionOrder,
  getProseControls,
} from '../src/utils/narrativeMomentum.service';
import {
  buildEffectiveProseInstruction,
  enforceForbiddenProseText,
} from '../src/utils/forbiddenProse.service';
import {
  assessSuspectedRepeatedRevelations,
  buildChapterCanonKnowledge,
  buildInspectorCanonKnowledgeBlock,
  buildScribeCanonKnowledgeBlock,
  buildSuspectedRevelationInspectorContext,
  ChapterCanonPreflightError,
  resolveConfirmedRepeatedRevelationRepair,
} from '../src/utils/chapterCanonKnowledge.service';
import { formatCanonEntriesForPrompt, formatCanonEntriesMultiline, formatCanonLocksForPrompt } from '../src/utils/canonInputNormalization.service';
import {
  assessSuspectedPhysicalContinuity,
  buildPhysicalContinuityInspectorContext,
  buildPhysicalContinuityContextSections,
  buildPhysicalContinuityLookbackSection,
  buildPhysicalContinuityScribeRule,
  markObligationsResolvedByChapter,
  resolveConfirmedPhysicalContinuityRepair,
} from '../src/utils/physicalContinuity.service';
import {
  PHYSICAL_EXTRACTION_CORRECTION_MAX,
  PhysicalContinuityExtractionError,
  type PhysicalContinuityExtractionValidation,
  ARCHIVIST_INITIAL_LEDGER_REQUIRED_FIELDS,
  ARCHIVIST_PHYSICAL_MANIFEST_INSTRUCTION,
  finalizePhysicalContinuityLedger,
  normalizeArchivistPhysicalResponse,
  normalizeInspectorPhysicalStateChanges,
  prepareChapterPhysicalCandidates,
  recordPhysicalCorrectionTransportAttempt,
} from '../src/utils/physicalContinuityExtraction.service';
import { runPhysicalArchivistCorrection } from '../src/utils/physicalContinuityCorrection.service';
import { mergeAuthoritativeLedger } from '../src/utils/authoritativeLedgerMerge.service';
import {
  deriveLatestStateDelta,
  latestStateDeltaToNarrativeStateDelta,
} from '../src/utils/latestStateDelta.service';
import { renderAuthoritativeContinuityPrompt } from '../src/utils/authoritativeContinuitySnapshot.service';
import {
  applySceneInsertions,
  buildChapterExpansionPlan,
  buildExpansionOutputMetadata,
  buildExpansionPlanPrompt,
  buildWordCountTargetPrompt,
  countChapterWords,
  detectOutputTruncation,
  evaluateExpansionPassResult,
  resolveChapterWordTargets,
  resolveMajorExpansionDispatch,
  splitChapterIntoScenes,
} from '../src/utils/chapterExpansion.service';
import {
  buildMajorChapterBuildPlan,
  executeMajorChapterBuild,
  formatMajorBuildAllowedMethods,
  toGeneratedExpansionScene,
} from '../src/utils/majorChapterBuild.service';
import {
  buildFourBeatAssemblyDiagnostic,
  parseLegacyScenesImport,
} from '../src/utils/fourBeatChapterAssembly.service';
import {
  buildFourBeatChapterContract,
  buildFourBeatContractPrompt,
} from '../src/utils/fourBeatChapterContract.service';
import {
  buildBeatRepairPrompt,
  executeFourBeatGenerationGate,
} from '../src/utils/fourBeatChapterExecution.service';
import { toGeneratedChapterBeat } from '../src/utils/fourBeatChapterValidation.service';
import {
  resolveScribeThinkingLevel,
  resolveScribeTransportMaxRetries,
  resolveMajorSceneOperation,
  resolveScribeOperationForMajorScene,
  type ScribeThinkingLevel,
} from '../src/utils/scribeThinkingPolicy.service';
import {
  buildLogicalFourBeatCallId,
  FourBeatCallBudgetTracker,
  generateFourBeatWithBoundedTransport,
} from '../src/utils/fourBeatTransport.service';
import {
  generateMajorSceneWithBoundedTransport,
} from '../src/utils/majorSceneTransport.service';
import type {
  ChapterExpansionPlan,
  ExpansionOutputMetadata,
  ExpansionPassResult,
  MajorBuildProgressEvent,
  MajorSceneOperation,
  SceneExpansionInsertion,
  ScribeOperation,
  ScribeSceneDiagnostic,
  AuthoritativeContinuitySnapshot,
} from '../src/types';
import { jsonrepair } from "jsonrepair";
import { serverGenerateWithRetry, assertServerTransportRequired, lastRequestId as transportLastRequestId } from "./geminiTransport";
import { runRoadmapPlanning } from '../src/utils/roadmapPlanning.runner';
import { buildSequelPlanningPromptAddendum } from '../src/utils/sequelPlanContract';
import type { RoadmapPlanningState } from '../src/types';

// ── Feature flag ──────────────────────────────────────────────────────────────
// VITE_USE_SERVER_GEMINI defaults to true (server transport).
// Set to 'false' only in local .env for development with a local key.

const formatEraState = (eraState: any) => {
  if (!eraState) return '';
  return `\n--- ERA STATE CONTEXT ---\n` +
         `Volume: ${eraState.currentVolume}\n` +
         `Era: ${eraState.currentEra}\n` +
         `Old System: ${eraState.oldSystem}\n` +
         `Current System: ${eraState.currentSystem}\n` +
         `Resolved Systems: ${(eraState.resolvedSystems || []).join(', ')}\n` +
         `Active Systems: ${(eraState.activeSystems || []).join(', ')}\n` +
         `Forbidden Era Confusions: ${(eraState.forbiddenEraConfusions || []).join(', ')}\n`;
};

const getVolume6Lock = (bible: StoryBible) => {
  if (bible?.volume === 6 && bible?.title?.toLowerCase().includes('ashen covenant')) {
    return `\n\nVOLUME 6 CURRENT STATE LOCK:
- Seren Caedrow is not normally physically alive unless explicitly restored by canon.
- Seren normally acts through spectral projection from the Memorial Arch / Aether-Web / Global Silk-Ley.
- If Seren interacts with mud, blood, cold, breath, lungs, boots, stone, physical pain, or physical injury, the text must explicitly explain whether this is:
  1. a temporary embodied projection,
  2. a memory-echo,
  3. symbolic sensory feedback through the Aether-Web,
  4. or an actual canon restoration.
- Do not casually depict Seren as an ordinary physical queen/patroller.

- Kael Caedrow remains a spectral anchor unless explicitly restored by canon.
- Do not depict Kael with ordinary physical wounds, boots, mud, blisters, bleeding, or bodily fatigue unless framed as projection/memory/symbolic feedback.

- Maelor Vance is not the old living High Flamekeeper ruling the Church.
- If Maelor appears, he must be framed as Remembered, legally resurrected/manifested, archival influence, memory, or post-Merge entity depending on canon.
- Do not depict him as simply alive in the old Church hierarchy unless explicitly justified.

- Active Volume 6 system:
  Aether-Web / Lattice Hub / Global Silk-Ley / micro-shrine network after the Decentralization Mandate.

- Active Volume 6 crisis:
  Harmonic Shear, Low-Void instability, Glass-Wraith formation, Aether-Solid anomalies, Lattice/micro-shrine failure modes.

- Resolved or historical systems:
  Ember Veil, Ash Blight, Zenith Spire, centralized Spire rule, Church dominance, Ghost-Fuel crisis, old sacrifice cycle.

- These resolved systems may appear only as:
  memory,
  historical record,
  trauma echo,
  ideological remnant,
  legal/religious legacy,
  or archived consequence.

- Do not write resolved systems as current dominant reality unless the chapter explicitly states a localized recurrence/anomaly and explains why it does not undo the prior resolution.\n`;
  }
  return '';
};

const getVolume6InspectorCheck = (bible: StoryBible) => {
  if (bible?.volume === 6 && bible?.title?.toLowerCase().includes('ashen covenant')) {
    return `\n\nINSPECTOR LORE COMPLIANCE CHECK (Volume 6): If Seren or Kael are written with physical body cues (mud, bleeding, breathing, physical pain, fatigue, boots, sweat), you MUST ask: "Is this a projection, memory-echo, sensory metaphor, temporary embodiment, or canon restoration?" If the answer is absent or unclear, return a correction order, not a broad deadlock.\n`;
  }
  return '';
};

const USE_SERVER_GEMINI: boolean = (
  (import.meta as any).env?.VITE_USE_SERVER_GEMINI !== 'false'
);

// ── Production safety ─────────────────────────────────────────────────────────
assertServerTransportRequired();

export const HUMAN_PROSE_CONSTRAINT = `CRITICAL HUMAN PROSE REQUIREMENT:
You must strictly avoid "AI-sounding" prose patterns.
- Do NOT use exact measurements (e.g., "three inches left", "forty-two degrees") unless contextually required.
- Do NOT overuse atmosphere/tone words like: physical weight, clinical, sterile, geometric, brutal, absolute, ancient, oppressive, mathematical, mechanical, calculus, economy.
- Do NOT use technical/physics terminology for action mechanics (e.g., center of gravity, kinetic transfer, torque, dominant base, friction lock, spatial geometry).
- Do NOT write dialogue that sounds like characters explaining the theme.
- Do NOT write long System or UI-style exposition dumps.
Write naturally, elegantly, and human-like. Focus on grounded, organic storytelling rather than clinical descriptions.`;

async function finalizeProseOutput(
  text: string | undefined,
  bible: StoryBible,
  agentStage: string,
): Promise<string> {
  const enforced = await enforceForbiddenProseText({
    text: text || '',
    bible,
    agentStage,
  });
  return enforced.text;
}

function appendForbiddenProseInstruction(instruction: string, bible: StoryBible): string {
  return `${instruction}\n\n${buildEffectiveProseInstruction(bible)}`;
}

export const WORKER_CONFIG = {
  FLASH: { model: GEMINI_MODEL_FLASH, thinkingLevel: null },
  ARCHITECT_ROADMAP: { model: GEMINI_MODEL_FLASH, thinkingLevel: null },
  CHAPTER_CONTRACT: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.LOW },
  SCRIBE: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.LOW },
  DIRECTOR_REWRITE: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.LOW },
  INSPECTOR: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.MEDIUM },
  SOFT_STATE: { model: GEMINI_MODEL_FLASH, thinkingLevel: null },
  EDITOR: { model: GEMINI_MODEL_PRO, thinkingLevel: ThinkingLevel.LOW },
  PUBLICATION_EDITOR: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.MEDIUM },
  ARCHIVIST: { model: GEMINI_MODEL_FLASH, thinkingLevel: ThinkingLevel.LOW },
  SEQUEL_EVOLUTION: { model: GEMINI_MODEL_FLASH, thinkingLevel: null }
};

export function resolveScribeOperationForChapter(correctionOrder?: string): ScribeOperation {
  return correctionOrder ? 'INSPECTOR_REWRITE' : 'INITIAL_CHAPTER';
}

export function resolveScribeThinkingConfig(operation: ScribeOperation): {
  thinkingLevel: ScribeThinkingLevel;
  transportMaxRetries: number;
} {
  const thinkingLevel = resolveScribeThinkingLevel(operation);
  return {
    thinkingLevel,
    transportMaxRetries: resolveScribeTransportMaxRetries(operation),
  };
}

const scribeSceneResponseSchema = {
  type: Type.OBJECT,
  properties: {
    content: { type: Type.STRING },
    summary: { type: Type.STRING },
    endingState: { type: Type.STRING },
    requiredBeatsSatisfied: { type: Type.ARRAY, items: { type: Type.STRING } },
    decisionPresent: { type: Type.BOOLEAN },
    consequencePresent: { type: Type.BOOLEAN },
  },
  required: ['content', 'summary', 'endingState', 'decisionPresent', 'consequencePresent'],
};

const fourBeatResponseSchema = {
  type: Type.OBJECT,
  properties: {
    title: { type: Type.STRING },
    summary: { type: Type.STRING },
    beats: {
      type: Type.ARRAY,
      description: 'Generate exactly 4 developed chapter beats in numeric sequence.',
      items: {
        type: Type.OBJECT,
        properties: {
          beatId: { type: Type.STRING },
          sequence: { type: Type.NUMBER },
          content: { type: Type.STRING },
          endingState: { type: Type.STRING },
          requiredEventsSatisfied: { type: Type.ARRAY, items: { type: Type.STRING }, description: 'Atomic event IDs satisfied in this beat (e.g. event-1).' },
          decisionPresent: { type: Type.BOOLEAN },
          consequencePresent: { type: Type.BOOLEAN },
        },
        required: ['beatId', 'sequence', 'content', 'endingState', 'decisionPresent', 'consequencePresent'],
      },
    },
  },
  required: ['title', 'beats', 'summary'],
};

const singleBeatRepairResponseSchema = {
  type: Type.OBJECT,
  properties: {
    beatId: { type: Type.STRING },
    sequence: { type: Type.NUMBER },
    content: { type: Type.STRING },
    endingState: { type: Type.STRING },
    requiredEventsSatisfied: { type: Type.ARRAY, items: { type: Type.STRING } },
    decisionPresent: { type: Type.BOOLEAN },
    consequencePresent: { type: Type.BOOLEAN },
  },
  required: ['beatId', 'sequence', 'content', 'endingState', 'decisionPresent', 'consequencePresent'],
};

import { buildInspectorAtomicEventContext, extractAtomicChapterEvents } from '../src/utils/fourBeatAtomicEvents.service';
import { assembleFourBeatChapter, FOUR_BEAT_SEPARATOR } from '../src/utils/fourBeatChapterAssembly.service';
import { buildFourBeatChapterContract } from '../src/utils/fourBeatChapterContract.service';
import { buildInspectorSummaryWarningContext, toGeneratedChapterBeat } from '../src/utils/fourBeatChapterValidation.service';
import {
  buildInspectorClassifiedBeat,
  type FourBeatChapterSession,
  mapInspectorResponseToValidationResult,
  normalizeInspectorBeatRepairRequests,
  splitAssembledChapterToBeats,
} from '../src/utils/fourBeatInspectorOrchestration.service';
import {
  analyzeInspectorResponseShape,
  logInspectorResponseShape,
  promoteRepairableInspectorVerdict,
} from '../src/utils/inspectorDecision.service';
import type { InspectorBeatRepairRequest } from '../src/types';

export { FourBeatStructuralFailureError, FourBeatCallBudgetExhaustedError } from '../src/types';
export { runInspectorWithOptionalBeatRepair, FOUR_BEAT_MAX_INSPECTOR_CALLS_PER_ATTEMPT } from '../src/utils/fourBeatInspectorOrchestration.service';
export type { FourBeatChapterSession, InspectorOrchestrationResult } from '../src/utils/fourBeatInspectorOrchestration.service';
import { registerFourBeatChapterSession } from '../src/utils/fourBeatSessionLifecycle.service';
import {
  buildPhysicalCorrectionResponseSchema,
  buildArchivistInitialPhysicalArrayProperties,
} from '../src/utils/physicalResponseSchema.service';
export {
  clearFourBeatChapterSession,
  getActiveFourBeatChapterSession,
  hasFourBeatV1Provenance,
  isFourBeatInspectorPass,
  isFourBeatV1ChapterContent,
  isFourBeatV1Orchestration,
  registerFourBeatChapterSession,
  shouldTerminateFourBeatInspectorPath,
  shouldUseLegacyInspectorCorrectionLoop,
  validateFourBeatSessionScope,
} from '../src/utils/fourBeatSessionLifecycle.service';

// ── Dev-only direct SDK path (used only when VITE_USE_SERVER_GEMINI=false) ───
// This block is tree-shaken out of the production bundle by Vite/esbuild
// because USE_SERVER_GEMINI is a compile-time constant when defined via define.
let _directSDKGenerate: ((params: any, timeoutMs?: number) => Promise<{ text: string }>) | null = null;

const getDirectSDK = async () => {
  if (!_directSDKGenerate) {
    // Dynamic import so the SDK is NOT included in the production bundle.
    const { GoogleGenAI, HarmCategory, HarmBlockThreshold } = await import('@google/genai');
    const apiKey =
      (typeof process !== 'undefined' && process.env?.GEMINI_API_KEY) ||
      (typeof process !== 'undefined' && process.env?.API_KEY) ||
      (import.meta as any).env?.VITE_GEMINI_API_KEY ||
      (import.meta as any).env?.VITE_API_KEY;
    if (!apiKey) throw new Error('[gemini] Dev SDK path: VITE_GEMINI_API_KEY not set. Set it in .env or switch to server transport.');
    const ai = new GoogleGenAI({ apiKey, httpOptions: { timeout: 600_000 } });
    const DEFAULT_SAFETY = [
      { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
      { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
      { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
      { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
    ];
    _directSDKGenerate = async (params: any) => {
      if (!params.config) params.config = {};
      if (!params.config.safetySettings) params.config.safetySettings = DEFAULT_SAFETY;
      if (params.model?.toLowerCase().includes('flash') && params.config.thinkingConfig) {
        delete params.config.thinkingConfig;
      }
      const res = await ai.models.generateContent(params);
      return { text: res.text ?? '' };
    };
  }
  return _directSDKGenerate!;
};

const MAX_RETRIES = 5;
const BASE_DELAY = 3000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const extractAndParseJSON = (text: string) => {
  if (!text) throw new Error("Empty response from AI");
  
  // 0. Remove thinking tags if present (including unclosed ones at the end)
  const cleanText = text.replace(/<thinking>[\s\S]*?(?:<\/thinking>|$)/gi, "").trim();

  const parseAndValidate = (content: string) => {
    const parsed = JSON.parse(jsonrepair(content));
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error("Parsed result is not an object or array");
    }
    return parsed;
  };

  // 1. Try markdown blocks
  const markdownRegex = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
  let match;
  while ((match = markdownRegex.exec(cleanText)) !== null) {
    const content = match[1].trim();
    try {
      return parseAndValidate(content);
    } catch (e) {
      // Ignore and try next block or fallback
    }
  }

  // 2. Find first balanced object/array
  const firstBrace = cleanText.indexOf('{');
  const firstBracket = cleanText.indexOf('[');
  let start = -1;

  if (firstBrace !== -1 && (firstBracket === -1 || firstBrace < firstBracket)) {
    start = firstBrace;
  } else if (firstBracket !== -1) {
    start = firstBracket;
  }

  if (start !== -1) {
    try {
      return parseAndValidate(cleanText.substring(start));
    } catch (e) {
      // Ignore and try fallback
    }
  }

  // 3. Final attempt
  try {
    return parseAndValidate(cleanText);
  } catch (error) {
    console.error("Final JSON Parse Attempt Failed. Text sample:", cleanText.substring(0, 500));
    throw new Error(`Failed to parse AI response as JSON: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export function extractProseDefensively(response: any): { text: string; shapeInfo: string; wordCount: number } {
  if (!response) {
    return { text: "", shapeInfo: "null/undefined", wordCount: 0 };
  }

  const type = typeof response;
  const keys = (response && typeof response === 'object') ? Object.keys(response) : [];

  let extractedText = "";

  if (typeof response === "string") {
    extractedText = response;
  } else if (response && typeof response === "object") {
    if (typeof response.text === "string") {
      extractedText = response.text;
    } else if (typeof response.content === "string") {
      extractedText = response.content;
    } else if (typeof response.draft === "string") {
      extractedText = response.draft;
    } else if (typeof response.finalText === "string") {
      extractedText = response.finalText;
    } else if (response.scenes && Array.isArray(response.scenes)) {
      extractedText = response.scenes.join('\n\n***\n\n');
    } else if (response.candidates && Array.isArray(response.candidates)) {
      const firstCand = response.candidates[0];
      if (firstCand && firstCand.content && Array.isArray(firstCand.content.parts)) {
        extractedText = firstCand.content.parts.map((p: any) => p.text || "").join("");
      } else if (firstCand && firstCand.content && typeof firstCand.content.text === "string") {
        extractedText = firstCand.content.text;
      }
    }
  }

  const cleanText = extractedText.trim();
  const wordCount = cleanText.split(/\s+/).filter(Boolean).length;
  const shapeInfo = `typeof: ${type}, keys: [${keys.join(", ")}], charLength: ${cleanText.length}, wordCount: ${wordCount}`;
  
  return { text: cleanText, shapeInfo, wordCount };
}

export let lastRequestId: string | undefined = undefined;
export const getLastRequestId = () => transportLastRequestId || lastRequestId;

// ── generateWithRetry: routes to server transport or dev SDK ─────────────────
// This is the only internal transport function. All agent calls go through here.
// Signatures of all exported functions above this are 100% preserved.
export const generateWithRetry = async (
  params: any,
  timeoutMs = 240000,
  transportOptions?: import('./geminiTransport').GenerateTransportOptions,
): Promise<{ text: string; requestId?: string; finishReason?: string; outputTokenCount?: number }> => {
  if (USE_SERVER_GEMINI) {
    const res = await serverGenerateWithRetry(params, {
      timeoutMs,
      ...transportOptions,
    });
    lastRequestId = res.requestId;
    return res;
  }

  // ── Dev direct SDK path (only when VITE_USE_SERVER_GEMINI=false) ──────────
  // Validate JSON output eagerly so the retry loop still works for parse failures.
  const directGenerate = await getDirectSDK();
  let lastError: unknown;
  for (let i = 0; i < MAX_RETRIES; i++) {
    try {
      const response = await directGenerate(params, timeoutMs);
      if (params.config?.responseMimeType === 'application/json') {
        extractAndParseJSON(response.text || '');
      }
      return response;
    } catch (error: any) {
      lastError = error;
      const msg = (error.message ?? String(error)).toLowerCase();
      const isRetryable =
        error?.status === 429 || error?.status === 500 || error?.status === 503 ||
        msg.includes('failed to parse ai response as json') ||
        msg.includes('empty response from ai') ||
        msg.includes('timed out') ||
        msg.includes('unavailable') ||
        msg.includes('high demand') ||
        msg.includes('503');
      if (isRetryable && i < MAX_RETRIES - 1) {
        console.warn(`[gemini] Dev SDK retry (${i + 1}/${MAX_RETRIES}):`, error.message);
        await delay(BASE_DELAY * Math.pow(2, i));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};

export const initializeStoryState = async (seed: StorySeed): Promise<Ledger & { factions?: Record<string, any>; worldRules?: Record<string, any>; styleRules?: Record<string, any>; canonLocks?: string }> => {
  const genreString = Array.isArray(seed.genre) ? seed.genre.map((g: any) => `${g.name} (${g.weight})`).join(', ') : (typeof seed.genre === 'string' ? seed.genre : '');
  const premiseString = `
    SURFACE CONFLICT: ${seed.premise.surface}
    UNDERLYING TENSION: ${seed.premise.tension}
    IRREVERSIBLE CHANGE: ${seed.premise.change}
  `;

  const prompt = `
    Analyze the story setup and construct a structured database.
    TITLE: ${seed.title}
    GENRE MATRIX: ${genreString}
    PREMISE DNA: ${premiseString}
    CHARACTER DOSSIERS: ${seed.characters}
    WORLD LORE: ${seed.worldLore}
    STYLE GUIDE: ${seed.style}

    CRITICAL EXTRACTION DIRECTIVES:
    1. EXTRACT ALL characters mentioned or clearly implied in the CHARACTER DOSSIERS, world lore, and setup. For each character, populate:
       - name (string)
       - age (string: must be structured value or exactly "needs confirmation". DO NOT invent any numerical age if it is not explicitly stated in the dossier or lore!)
       - role (string: e.g. Protagonist, Antagonist, Supporting)
       - faction (string: the faction/group they belong to)
       - motivation (string: their key drive)
       - fear (string: their primary fear/wound)
       - relationship (string: key relationships/dynamics)
       - location (string: where they are at the start of the story)
       - abilities (string: special traits/skills)
       - limitations (string: key weaknesses/limitations)
       - canon_locks (string: hard immutable facts about them)
       - forbidden_changes (string: changes that must not happen to them)
    
    2. EXTRACT ALL factions and groups mentioned. For each faction, populate:
       - name (string)
       - goal (string)
       - public_belief (string)
       - hidden_truth (string)
       - leader (string)
       - methods (string)
       - relationship_to_protagonist (string)
       - canon_locks (string)

    3. EXTRACT World Rules & Lore cards/sections:
       - id/title (string: e.g. "Magic Rules", "Technology Level", "Political Rules")
       - content (string: detailed rules/constraints)

    4. EXTRACT Style Rules:
       - tone_style (string: tone constraints)
       - dialogue_style (string: naturalism details)
       - forbidden_words (string: words like shattered, visceral, etc.)
       - constraints (string: modern term restrictions)

    5. EXTRACT real, project-specific Canon Locks. Absolutely NO placeholders or generic rules from other books (like Mara or aether-magic). If none are defined, leave this field empty.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHIVIST.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHIVIST + "\nYou are a world-class narrative architect. Construct a clean, extremely high-fidelity story blueprint extraction matching the specified JSON schema completely. Do not include placeholders.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING },
          factions: { type: Type.OBJECT },
          worldRules: { type: Type.OBJECT },
          styleRules: {
            type: Type.OBJECT,
            properties: {
              tone_style: { type: Type.STRING },
              dialogue_style: { type: Type.STRING },
              forbidden_words: { type: Type.STRING },
              constraints: { type: Type.STRING }
            }
          },
          canonLocks: { type: Type.STRING }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel }
    },
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  
  // Normalization logic for possible AI backend field alias responses
  let charData = rawData.characterCards || rawData.characters || rawData.cast || rawData.dossiers || {};
  let factionData = rawData.factions || rawData.factionCards || rawData.factionsAndGroups || rawData.groups || {};
  let worldData = rawData.worldRules || rawData.worldLore || rawData.loreCards || rawData.rules || {};
  const lockData = rawData.canonLocks || rawData.hardCanonLocks || rawData.locks || "";
  const styleData = rawData.styleRules || rawData.writingStyleRules || rawData.style || {};
  const roadmapData = rawData.chapterPlans || rawData.roadmap || [];

  // Normalize character data if it is an array
  if (Array.isArray(charData)) {
    const charObj: Record<string, any> = {};
    charData.forEach((c: any) => {
      if (c && typeof c === 'object') {
        const nameKey = c.name || c.id || `Character_${Math.random().toString(36).substring(2, 6)}`;
        charObj[nameKey] = c;
      }
    });
    charData = charObj;
  }

  // Normalize faction data if it is an array
  if (Array.isArray(factionData)) {
    const factionObj: Record<string, any> = {};
    factionData.forEach((f: any) => {
      if (f && typeof f === 'object') {
        const nameKey = f.name || f.id || `Faction_${Math.random().toString(36).substring(2, 6)}`;
        factionObj[nameKey] = f;
      }
    });
    factionData = factionObj;
  }

  // Normalize worldRules data if it is an array or string-to-string dictionary
  if (Array.isArray(worldData)) {
    const worldObj: Record<string, any> = {};
    worldData.forEach((w: any) => {
      if (w && typeof w === 'object') {
        const titleKey = w.title || w.id || w.name || `Rule_${Math.random().toString(36).substring(2, 6)}`;
        worldObj[titleKey] = {
          title: titleKey,
          content: w.content || w.desc || w.description || JSON.stringify(w)
        };
      }
    });
    worldData = worldObj;
  } else if (worldData && typeof worldData === 'object') {
    const worldObj: Record<string, any> = {};
    Object.entries(worldData).forEach(([key, val]) => {
      if (typeof val === 'string') {
        worldObj[key] = { title: key, content: val };
      } else if (val && typeof val === 'object') {
        worldObj[key] = {
          title: (val as any).title || (val as any).id || key,
          content: (val as any).content || (val as any).desc || (val as any).description || JSON.stringify(val)
        };
      }
    });
    worldData = worldObj;
  }

  return {
    ...rawData,
    characters: charData,
    locations: rawData.locations || {},
    inventory: Array.isArray(rawData.inventory) ? rawData.inventory : [],
    plot_points: Array.isArray(rawData.plot_points) ? rawData.plot_points : [],
    resolved_plot_points: Array.isArray(rawData.resolved_plot_points) ? rawData.resolved_plot_points : [],
    momentum: rawData.momentum || "Neutral",
    arc_stage: rawData.arc_stage || "Beginning",
    current_time: rawData.current_time || "Day 1",
    last_updated: new Date().toISOString(),
    factions: factionData,
    worldRules: worldData,
    styleRules: styleData,
    canonLocks: lockData,
    chapterPlans: roadmapData
  };
};

export const extractStoryBlueprint = async (seed: StorySeed): Promise<any> => {
  const genreString = Array.isArray(seed.genre) ? seed.genre.map((g: any) => `${g.name} (${g.weight})`).join(', ') : (typeof seed.genre === 'string' ? seed.genre : '');
  const premiseString = `
    SURFACE CONFLICT: ${seed.premise.surface}
    UNDERLYING TENSION: ${seed.premise.tension}
    IRREVERSIBLE CHANGE: ${seed.premise.change}
  `;

  const prompt = `
    You are a world-class narrative architect. Analyze the story setup and extract a complete, cohesive Story Blueprint matching the requested JSON schema EXACTLY.
    
    TITLE: ${seed.title}
    GENRE MATRIX: ${genreString}
    PREMISE DNA: ${premiseString}
    CHARACTER DOSSIERS: ${seed.characters}
    WORLD LORE: ${seed.worldLore}
    STYLE GUIDE: ${seed.style}

    CRITICAL EXTRACTION DIRECTIVES:
      0. EXTRACTION INTEGRITY:
         - Do NOT invent missing structural facts. If it is not present in the text, leave it blank.
         - Missing information MUST be blank or omitted.
         - Ambiguous information MUST be identified as uncertain.
         - Inferred facts must NOT be marked as user-supplied.
         - You are merely extracting the user's intent. Do not approve fields or artificially fill them to improve sufficiency scores.

      1. PROJECT OVERVIEW:
       Extract title, genre list, format (e.g. ${seed.mode}), targetChapterCount (${seed.targetChapters}), and chapterLength (${seed.chapterLength}).

    2. CORE STORY DNA:
       Analyze and break down the premise, main conflict, theme/deeper tension, and ending direction.

    3. CHARACTER CARDS:
       Extract ALL characters mentioned or clearly implied in the CHARACTER DOSSIERS, world lore, and setup. For each character, populate:
       - name
       - age (string: e.g. "needs confirmation" or numeric if explicitly stated)
       - role (Protagonist, Antagonist, Supporting)
       - factionOrGroup
       - motivation
       - fearOrWound
       - relationshipNotes
       - currentLocation
       - abilities
       - limitations
       - canonLocks (array of strings representing immutable facts about them)
       - forbiddenChanges (array of strings representing modifications that must never happen to them)

    4. FACTIONS & GROUPS:
       Extract ALL factions, organizations, or groups mentioned. For each, populate their goals, public belief, hidden truth, operational methods, relationship to the protagonist, and specific canon locks.

    5. WORLD RULES & LORE:
       Extract rules and lore constraints. Populate:
       - name (e.g., "Magic Rules", "Technology Level", "Black Flame Lore")
       - category (e.g., "Magic System", "History", "Sociology")
       - description
       - limits (what are the specific cost, limits, or hard boundaries?)
       - forbiddenContradictions (array of rules that must never be broken)

    6. WRITING STYLE RULES:
       Extract specific tone and style constraints, dialogue styles, forbidden words, and prose precision constraints.

    7. CANON LOCKS:
       Extract project-specific hard canon locks as an array of strings. Do not include generic placeholder locks.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHIVIST.model,
    contents: prompt,
    config: {
      systemInstruction: "You are THE NARRATIVE ARCHITECT. You construct structural databases for stories. You must output the Story Blueprint strictly matching the provided JSON response schema.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          projectOverview: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              genre: { type: Type.ARRAY, items: { type: Type.STRING } },
              format: { type: Type.STRING },
              targetChapterCount: { type: Type.NUMBER },
              chapterLength: { type: Type.STRING }
            },
            required: ["title", "genre", "format", "targetChapterCount", "chapterLength"]
          },
          coreStory: {
            type: Type.OBJECT,
            properties: {
              premise: { type: Type.STRING },
              mainConflict: { type: Type.STRING },
              deeperTension: { type: Type.STRING },
              theme: { type: Type.STRING },
              permanentChange: { type: Type.STRING },
              endingDirection: { type: Type.STRING }
            },
            required: ["premise", "mainConflict", "deeperTension", "theme", "permanentChange", "endingDirection"]
          },
          characterCards: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                age: { type: Type.STRING },
                role: { type: Type.STRING },
                factionOrGroup: { type: Type.STRING },
                motivation: { type: Type.STRING },
                fearOrWound: { type: Type.STRING },
                relationshipNotes: { type: Type.STRING },
                currentLocation: { type: Type.STRING },
                abilities: { type: Type.STRING },
                limitations: { type: Type.STRING },
                canonLocks: { type: Type.ARRAY, items: { type: Type.STRING } },
                forbiddenChanges: { type: Type.ARRAY, items: { type: Type.STRING } }
              },
              required: ["name", "age", "role", "factionOrGroup"]
            }
          },
          factions: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                goal: { type: Type.STRING },
                publicBelief: { type: Type.STRING },
                hiddenTruth: { type: Type.STRING },
                leader: { type: Type.STRING },
                methods: { type: Type.STRING },
                relationshipToProtagonist: { type: Type.STRING },
                canonLocks: { type: Type.ARRAY, items: { type: Type.STRING } }
              },
              required: ["name", "goal"]
            }
          },
          worldRules: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                category: { type: Type.STRING },
                description: { type: Type.STRING },
                limits: { type: Type.STRING },
                forbiddenContradictions: { type: Type.ARRAY, items: { type: Type.STRING } }
              },
              required: ["name", "category", "description"]
            }
          },
          writingStyleRules: {
            type: Type.OBJECT,
            properties: {
              toneAndProse: { type: Type.STRING },
              dialogueStyle: { type: Type.STRING },
              forbiddenWordsAndPhrases: { type: Type.ARRAY, items: { type: Type.STRING } },
              prosePrecisionConstraints: { type: Type.STRING }
            },
            required: ["toneAndProse", "dialogueStyle", "forbiddenWordsAndPhrases", "prosePrecisionConstraints"]
          },
          canonLocks: { type: Type.ARRAY, items: { type: Type.STRING } },
          chapterRoadmap: { type: Type.ARRAY, items: { type: Type.OBJECT } }
        },
        required: ["projectOverview", "coreStory", "characterCards", "factions", "worldRules", "writingStyleRules", "canonLocks"]
      }
    }
  });

  return extractAndParseJSON(response.text || "{}");
};

export const evolveLedgerForSequel = async (
  currentLedger: Ledger,
  bible: StoryBible,
  chapters: Chapter[] = [],
): Promise<Ledger> => {
  const { buildSequelLedgerProjection } = await import('../src/utils/sequelLedgerProjection');
  const projection = buildSequelLedgerProjection(currentLedger, bible, chapters);

  const prompt = `
    EVOLVE LEDGER FOR VOLUME ${bible.volume + 1}.
    Continuity projection (active canon only):
    ${JSON.stringify(projection)}
    
    1. Archive all plot_points and resolved_plot_points from the previous volume into 'historical_events'. Clear plot_points and resolved_plot_points.
    2. Update character descriptions based on their development in the previous arc.
    3. Reset momentum and arc_stage for a new story beginning.
    4. Keep the world lore intact but update current locations if they changed.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SEQUEL_EVOLUTION.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHIVIST + "\nPerform a clean multi-book transition. Ensure continuity is preserved while creating a fresh starting state for a new volume.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          historical_events: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SEQUEL_EVOLUTION.thinkingLevel }
    },
  }, 58_000, {
    policy: 'sequel_step',
    operation: 'sequel_evolve_ledger',
    agent: 'Archivist',
    timeoutMs: 58_000,
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  
  const inventory = Array.isArray(rawData.inventory) ? rawData.inventory : currentLedger.inventory || [];
  const plot_points = Array.isArray(rawData.plot_points) ? rawData.plot_points : [];
  const resolved_plot_points = Array.isArray(rawData.resolved_plot_points) ? rawData.resolved_plot_points : [];
  const historical_events = Array.isArray(rawData.historical_events) ? rawData.historical_events : [];

  return {
    ...currentLedger,
    ...rawData,
    inventory,
    plot_points,
    resolved_plot_points,
    historical_events,
    characters: {
      ...(currentLedger.characters || {}),
      ...(rawData.characters || {})
    },
    locations: {
      ...(currentLedger.locations || {}),
      ...(rawData.locations || {})
    },
    last_updated: new Date().toISOString()
  };
};

export const evolveBibleForSequel = async (currentBible: StoryBible): Promise<StoryBible> => {
  const prompt = `
    EVOLVE STORY BIBLE FOR VOLUME ${currentBible.volume + 1}.
    Current Premise:
    SURFACE: ${currentBible.premise.surface}
    TENSION: ${currentBible.premise.tension}
    CHANGE: ${currentBible.premise.change}

    1. Propose a new Premise for the sequel. The new premise should build upon the previous volume's resolution but introduce a new central conflict.
    2. Retain the title, genre, style guide, mode, target chapter count, and chapter length.
    3. Clear out any old chapter plans.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SEQUEL_EVOLUTION.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHITECT + "\nYou are planning the next volume in a series. Create a compelling new premise.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          premise: {
            type: Type.OBJECT,
            properties: {
              surface: { type: Type.STRING },
              tension: { type: Type.STRING },
              change: { type: Type.STRING }
            },
            required: ["surface", "tension", "change"]
          }
        },
        required: ["premise"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SEQUEL_EVOLUTION.thinkingLevel }
    },
  }, 58_000, {
    policy: 'sequel_step',
    operation: 'sequel_evolve_bible',
    agent: 'Architect',
    timeoutMs: 58_000,
  });

  const rawData = extractAndParseJSON(response.text || "{}");

  return {
    ...currentBible,
    volume: currentBible.volume + 1,
    premise: rawData.premise || currentBible.premise,
    chapterPlans: [], // Clear old plans
    isClosingMode: false
  };
};

export const regenerateSequelAiProposal = async (
  currentBible: StoryBible,
  authorAddendum = '',
): Promise<{ surface: string; tension: string; change: string }> => {
  const prompt = `
    PROPOSE SEQUEL NARRATIVE DNA FOR VOLUME ${currentBible.volume}.
    Current inherited premise:
    SURFACE: ${currentBible.premise.surface}
    TENSION: ${currentBible.premise.tension}
    CHANGE: ${currentBible.premise.change}

    ${authorAddendum}

    Return only a fresh proposal triad. Do not create chapter plans or canonize new world systems yet.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SEQUEL_EVOLUTION.model,
    contents: prompt,
    config: {
      systemInstruction:
        SYSTEM_INSTRUCTIONS.ARCHITECT +
        '\nPropose sequel narrative DNA only. This is an unapproved proposal — do not treat output as canon.',
      responseMimeType: 'application/json',
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          premise: {
            type: Type.OBJECT,
            properties: {
              surface: { type: Type.STRING },
              tension: { type: Type.STRING },
              change: { type: Type.STRING },
            },
            required: ['surface', 'tension', 'change'],
          },
        },
        required: ['premise'],
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SEQUEL_EVOLUTION.thinkingLevel },
    },
  }, 58_000, {
    policy: 'sequel_step',
    operation: 'sequel_propose_premise',
    agent: 'Architect',
    timeoutMs: 58_000,
  });

  const rawData = extractAndParseJSON(response.text || '{}');
  const premise = rawData.premise || currentBible.premise;
  return {
    surface: premise.surface || currentBible.premise.surface,
    tension: premise.tension || currentBible.premise.tension,
    change: premise.change || currentBible.premise.change,
  };
};

export const planChapters = async (
  bible: StoryBible,
  ledger: Ledger,
  onProgress?: (msg: string) => void,
  options?: {
    existingPlanningState?: RoadmapPlanningState | null;
    onPlanningStateChange?: (state: RoadmapPlanningState) => void | Promise<void>;
  },
): Promise<{ plans: ChapterPlan[]; warnings: any[]; planningState: RoadmapPlanningState }> => {
  const genreString = Array.isArray(bible.genre)
    ? bible.genre.map((g: any) => `${g.name} (${g.weight})`).join(', ')
    : typeof bible.genre === 'string'
      ? bible.genre
      : '';
  const premiseString = `
    SURFACE: ${bible.premise.surface}
    TENSION: ${bible.premise.tension}
    CHANGE: ${bible.premise.change}
  `;
  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode);
  const targetCount = bible.targetChapterCount;

  return runRoadmapPlanning({
    bible: {
      ...bible,
      roadmapPlanning: options?.existingPlanningState ?? bible.roadmapPlanning ?? null,
    },
    ledger,
    onProgress,
    onStateChange: options?.onPlanningStateChange,
    parseChunkResponse: (rawText) => extractAndParseJSON(rawText || '{}'),
    generateChunk: async ({ range, previousPlans }) => {
      const previousContext =
        previousPlans.length > 0
          ? `PREVIOUS CHAPTERS:\n${previousPlans
              .map((p) => `CH ${p.chapterNumber}: ${p.title}\n${p.summary}`)
              .join('\n\n')}`
          : 'NO PREVIOUS CHAPTERS YET.';

      const premadeRoadmapContext = bible.premadeRoadmap
        ? `IMPORTANT PRE-MADE ROADMAP: The user has explicitly provided the following custom roadmap. You MUST adhere to the overarching arc outlined here rather than inventing your own completely new arc. Use it as the source of truth for the plot: \n\n${bible.premadeRoadmap}\n\n`
        : '';

      const sequelPlanningContext = buildSequelPlanningPromptAddendum(bible.sequelPlan, null);
      const sequelContextBlock = sequelPlanningContext
        ? `SEQUEL AUTHOR PLAN (BINDING PRIORITY):\n${sequelPlanningContext}\n\n`
        : '';

      const prompt = `
      PLAN CHAPTERS ${range.startChapter} to ${range.endChapter} (out of ${targetCount}) for a ${bible.mode} project.
      TITLE: ${bible.title}
      GENRES: ${genreString}
      PREMISE: ${premiseString}
      STYLE GUIDE: ${bible.style_guide}
      INITIAL LEDGER: ${JSON.stringify(ledger)}
      
      ${sequelContextBlock}
      ${premadeRoadmapContext}
      ${previousContext}
      
      Your task is to generate the plans ONLY for chapters ${range.startChapter} through ${range.endChapter}.
      Ensure they seamlessly continue from the previous chapters (if any) and move towards a satisfying conclusion at chapter ${targetCount}.
      
      CRITICAL REMINDER: You must ensure all characters from the INITIAL LEDGER are utilized across the entire roadmap. Avoid focusing entirely on the protagonist at the expense of supporting characters. Double-check that all established characters are woven into the chapter plans you are generating.
    `;

      const response = await generateWithRetry(
        {
          model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
          contents: prompt,
          config: {
            systemInstruction: instruction,
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                plans: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      chapterNumber: { type: Type.NUMBER },
                      title: { type: Type.STRING },
                      pov: { type: Type.STRING },
                      summary: { type: Type.STRING },
                      requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
                      forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
                      continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                      characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                      locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                    },
                    required: ['chapterNumber', 'title', 'summary', 'requiredBeats'],
                  },
                },
              },
              required: ['plans'],
            },
            thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
          },
        },
        240_000,
        {
          policy: 'standard',
          operation: `roadmap_chunk_${range.startChapter}_${range.endChapter}`,
          agent: 'Architect',
        },
      );

      return {
        rawText: response.text || '{}',
        requestId: response.requestId || transportLastRequestId || lastRequestId,
      };
    },
  });
};

export const regenerateRemainingRoadmap = async (
  bible: StoryBible, 
  ledger: Ledger, 
  existingPlans: ChapterPlan[], 
  fromIndex: number,
  targetChapterCount: number,
  onProgress?: (msg: string) => void
): Promise<ChapterPlan[]> => {
  const genreString = Array.isArray(bible.genre) ? bible.genre.map((g: any) => `${g.name} (${g.weight})`).join(', ') : (typeof bible.genre === 'string' ? bible.genre : '');
  const premiseString = `
    SURFACE: ${bible.premise.surface}
    TENSION: ${bible.premise.tension}
    CHANGE: ${bible.premise.change}
  `;

  const safePlansMap = new Map<number, ChapterPlan>();
  existingPlans.forEach((p, idx) => {
    if (idx < fromIndex || p.status === 'locked' || p.status === 'drafted') {
      safePlansMap.set(p.chapterNumber, p);
    }
  });

  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode);
  
  const BATCH_SIZE = 10;
  let newlyGeneratedPlans: any[] = [];
  
  // Find all chapters that actually need generation
  const chaptersToGenerate: number[] = [];
  for (let i = 1; i <= targetChapterCount; i++) {
    if (!safePlansMap.has(i)) {
      chaptersToGenerate.push(i);
    }
  }

  // Iterate over batches
  for (let i = 0; i < chaptersToGenerate.length; i += BATCH_SIZE) {
    const currentBatch = chaptersToGenerate.slice(i, i + BATCH_SIZE);
    const startIdx = currentBatch[0];
    const endIdx = currentBatch[currentBatch.length - 1];

    if (onProgress) {
      onProgress(`Regenerating Roadmap chunk: Chapters ${startIdx} to ${endIdx}...`);
    }

    const lockedContext = safePlansMap.size > 0 
      ? `LOCKED INCORPORATED CHAPTERS:\n${Array.from(safePlansMap.values()).sort((a,b) => a.chapterNumber - b.chapterNumber).map(p => `CH ${p.chapterNumber}: ${p.title}\n${p.summary}`).join('\n\n')}` 
      : 'NO LOCKED CHAPTERS.';
      
    const recentContext = newlyGeneratedPlans.length > 0 
      ? `RECENTLY GENERATED CHAPTERS IN THIS RUN:\n${newlyGeneratedPlans.map(p => `CH ${p.chapterNumber}: ${p.title}\n${p.summary}`).join('\n\n')}`
      : 'NO RECENTLY GENERATED CHAPTERS YET.';

    const premadeRoadmapContext = bible.premadeRoadmap 
      ? `IMPORTANT PRE-MADE ROADMAP: The user has explicitly provided the following custom roadmap. Adhere to it: \n\n${bible.premadeRoadmap}\n\n`
      : '';

    const prompt = `
      You are the Architect. The user wants to regenerate their chapter roadmap.
      
      PROJECT GOAL: A continuous story of EXACTLY ${targetChapterCount} chapters.
      
      TITLE: ${bible.title}
      GENRES: ${genreString}
      PREMISE: ${premiseString}
      STYLE GUIDE: ${bible.style_guide}
      INITIAL LEDGER: ${JSON.stringify(ledger)}
      
      ${premadeRoadmapContext}
      ${lockedContext}
      
      ${recentContext}
      
      Your task:
      Generate plans ONLY for the following specific chapters: ${currentBatch.join(', ')}.
      Ensure the arc connects perfectly with the locked and recently generated plans, resolving compellingly by the last chapter.
      Output ONLY the plans for these specific requested chapters.
      For each requested chapter, provide its chapterNumber, title, pov, summary, and requiredBeats.
      
      CRITICAL REMINDER: You must ensure all characters from the INITIAL LEDGER are utilized across the remaining roadmap. Avoid focusing entirely on the protagonist at the expense of supporting characters. Double-check that all established characters are woven into these new chapter plans.
    `;

    const response = await generateWithRetry({
      model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
      contents: prompt,
      config: {
        systemInstruction: instruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            plans: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  chapterNumber: { type: Type.NUMBER },
                  title: { type: Type.STRING },
                  pov: { type: Type.STRING },
                  summary: { type: Type.STRING },
                  requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
                  forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
                  continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                  characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                  locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } }
                },
                required: ["chapterNumber", "title", "summary", "requiredBeats"]
              }
            }
          },
          required: ["plans"]
        },
        thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
      },
    });

    const rawData = extractAndParseJSON(response.text || "{}");
    const plans = Array.isArray(rawData.plans) ? rawData.plans : [];
    
    const mappedPlans = plans.map((p: any) => ({
      ...p,
      id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: "planned",
      updatedAt: new Date().toISOString()
    }));
    
    newlyGeneratedPlans = [...newlyGeneratedPlans, ...mappedPlans];
  }

  const finalPlans: ChapterPlan[] = [];
  for (let i = 1; i <= targetChapterCount; i++) {
    if (safePlansMap.has(i)) {
      finalPlans.push(safePlansMap.get(i)!);
    } else {
      const generated = newlyGeneratedPlans.find(p => p.chapterNumber === i);
      if (generated) {
        finalPlans.push(generated);
      } else {
        // Fallback if AI missed it
        finalPlans.push({
          id: `plan_fallback_${Date.now()}_${i}`,
          chapterNumber: i,
          title: `Chapter ${i}`,
          summary: 'Advance the plot.',
          requiredBeats: [],
          status: 'planned' as const,
          updatedAt: new Date().toISOString()
        });
      }
    }
  }

  return finalPlans;
};

export const regenerateSingleChapterPlan = async (
  bible: StoryBible,
  ledger: Ledger,
  existingPlans: ChapterPlan[],
  targetIndex: number
): Promise<ChapterPlan> => {
  const genreString = Array.isArray(bible.genre) ? bible.genre.map((g: any) => `${g.name} (${g.weight})`).join(', ') : (typeof bible.genre === 'string' ? bible.genre : '');
  const premiseString = `
    SURFACE: ${bible.premise.surface}
    TENSION: ${bible.premise.tension}
    CHANGE: ${bible.premise.change}
  `;

  const previousPlans = existingPlans.slice(0, targetIndex);
  const futurePlans = existingPlans.slice(targetIndex + 1);
  const targetChapterNum = targetIndex + 1;

  const premadeRoadmapContext = bible.premadeRoadmap 
    ? `IMPORTANT PRE-MADE ROADMAP: The user has explicitly provided the following custom roadmap. Connect the arc according to it: \n\n${bible.premadeRoadmap}\n\n`
    : '';

  const prompt = `
    You are the Architect. The user wants to perfectly regenerate ONE specific chapter plan (Chapter ${targetChapterNum}) to bridge the gap between the established previous chapters and the planned future chapters.
    
    TITLE: ${bible.title}
    GENRES: ${genreString}
    PREMISE: ${premiseString}
    STYLE GUIDE: ${bible.style_guide}
    INITIAL LEDGER: ${JSON.stringify(ledger)}
    
    ${premadeRoadmapContext}
    
    PREVIOUS CANON PLANS:
    ${previousPlans.map(p => `CH ${p.chapterNumber}: ${p.title} - ${p.summary}`).join('\n')}
    
    FUTURE DESTINATION PLANS:
    ${futurePlans.map(p => `CH ${p.chapterNumber}: ${p.title} - ${p.summary}`).join('\n')}
    
    Your task:
    Generate exactly ONE chapter plan for Chapter ${targetChapterNum} that seamlessly connects the PREVIOUS CANON to the FUTURE DESTINATION. The required beats must advance the character and story meaningfully toward the destination.
    Output ONLY the JSON for this single chapter.
  `;

  const instruction = SYSTEM_INSTRUCTIONS.ARCHITECT.replace('{{MODE}}', bible.mode);

  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHITECT_ROADMAP.model,
    contents: prompt,
    config: {
      systemInstruction: instruction,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          chapterNumber: { type: Type.NUMBER },
          title: { type: Type.STRING },
          pov: { type: Type.STRING },
          summary: { type: Type.STRING },
          requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
          forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
          continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
          characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
          locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["chapterNumber", "title", "summary", "requiredBeats"]
      },
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHITECT_ROADMAP.thinkingLevel },
    },
  });

  const parsed = extractAndParseJSON(response.text || "{}");
  return {
    ...parsed,
    id: `plan_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    status: "planned",
    updatedAt: new Date().toISOString(),
    chapterNumber: targetChapterNum
  };
};

export const retrieveSoftState = async (
  storyBible: StoryBible,
  ledger: Ledger,
  chapterNum: number,
  previousChapters: Chapter[]
): Promise<SoftStateBrief | null> => {
  if (chapterNum <= 1 || previousChapters.length === 0) return null;

  const relevantChapters = previousChapters.slice(-3); // Get last 1 to 3 chapters
  const chapterPlan = storyBible.chapterPlans?.find(p => p?.chapterNumber === chapterNum);
  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => l.name);
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active') || activeLocations.includes(c.location)).map((c: any) => c.name);

  const prompt = `
    You are the Soft State Retrieval Engine for a story generation system.
    Your job is to extract recent semantic texture, atmospheric details, emotional residue, and character voice notes from the immediately preceding chapters. This helps the Scribe maintain continuity in the upcoming chapter without overriding strictly factual canon.
    
    UPCOMING CHAPTER CONTEXT:
    Chapter number: ${chapterNum}
    Active characters: ${activeCharacters.join(', ')}
    Active locations: ${activeLocations.join(', ')}
    Chapter Objective: ${chapterPlan?.summary || 'Unknown'}
    
    RECENT PRIOR CHAPTERS (Source Material):
    ${relevantChapters.map(c => `--- CHAPTER ${c.id}: ${c.title} ---\n${c.content}\n`).join('\n')}
    
    Extract a concise SoftStateBrief. Ignore massive plot points (Ledger handles that). Focus entirely on atmosphere, dialogue cadence, emotional carryover, recurring sensory cues, motifs to preserve slightly, and phrases to avoid repeating.
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.SOFT_STATE.model,
    contents: prompt,
    config: {
      systemInstruction: "You are the Soft State Memory layer. Be concise, extracting only atmosphere, voice, and sensory residue that naturally flows into the next immediate chapter. This is an aid for the Scribe.",
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.SOFT_STATE.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          recentSensoryCarryover: { type: Type.STRING },
          activeEmotionalResidue: { type: Type.STRING },
          voiceNotes: { type: Type.STRING },
          environmentalContinuityCues: { type: Type.STRING },
          motifsToLightlyPreserve: { type: Type.STRING },
          antiRepetitionWarnings: { type: Type.STRING },
        },
        required: [
          "recentSensoryCarryover", 
          "activeEmotionalResidue", 
          "voiceNotes", 
          "environmentalContinuityCues", 
          "motifsToLightlyPreserve", 
          "antiRepetitionWarnings"
        ]
      }
    }
  });

  return extractAndParseJSON(response.text || "{}") as SoftStateBrief;
};

async function invokeTargetedFourBeatRepair(params: {
  chapterNum: number;
  contract: import('../src/types').FourBeatChapterContract;
  repairRequest: import('../src/utils/fourBeatChapterExecution.service').BeatRepairRequest;
  callBudget: import('../src/utils/fourBeatTransport.service').FourBeatCallBudgetTracker;
  systemInstruction: string;
  model: string;
  planContext: string;
  softStateContext: string;
  storyBible: StoryBible;
  authoritativeContinuityContext?: string;
}) {
  const repairThinking = resolveScribeThinkingConfig('INSPECTOR_REWRITE');
  const repairPrompt = buildBeatRepairPrompt({
    ...params.repairRequest,
    storyContext: [
      params.authoritativeContinuityContext || '',
      params.planContext,
      params.softStateContext,
    ].filter(Boolean).join('\n'),
  });
  const repairLogicalCallId = buildLogicalFourBeatCallId({
    chapterNumber: params.chapterNum,
    beatId: params.repairRequest.contractBeat.beatId,
    scribeOperation: 'INSPECTOR_REWRITE',
    narrativeAttempt: 1,
  });
  const repairResponse = await generateFourBeatWithBoundedTransport(
    {
      model: params.model,
      contents: repairPrompt,
      config: {
        systemInstruction: params.systemInstruction + '\nACT AS A TARGETED BEAT REPAIR ENGINE. ONE BEAT ONLY.\n' + buildEffectiveProseInstruction(params.storyBible),
        thinkingConfig: { thinkingLevel: repairThinking.thinkingLevel },
        temperature: 0.65,
        maxOutputTokens: 8192,
        responseMimeType: 'application/json',
        responseSchema: singleBeatRepairResponseSchema,
      },
    },
    {
      logicalCallId: repairLogicalCallId,
      chapterNumber: params.chapterNum,
      beatId: params.repairRequest.contractBeat.beatId,
      scribeOperation: 'INSPECTOR_REWRITE',
      narrativeAttempt: 1,
      generationPolicy: 'four_beat_repair',
      thinkingLevel: repairThinking.thinkingLevel,
    },
    {
      callBudget: params.callBudget,
      onDiagnostic: (diagnostic) =>
        console.log(`[FOUR BEAT TRANSPORT] ${JSON.stringify(diagnostic)}`),
    },
  );
  const repairJson = extractAndParseJSON(repairResponse.text || '');
  const cleanedContent = await finalizeProseOutput(
    repairJson.content,
    params.storyBible,
    'four_beat_repair',
  );
  return toGeneratedChapterBeat({
    beatId: params.repairRequest.contractBeat.beatId,
    sequence: params.repairRequest.contractBeat.sequence,
    content: cleanedContent,
    endingState: repairJson.endingState,
    requiredEventsSatisfied: repairJson.requiredEventsSatisfied,
    decisionPresent: repairJson.decisionPresent,
    consequencePresent: repairJson.consequencePresent,
    finishReason: repairResponse.finishReason,
    outputTokenCount: repairResponse.outputTokenCount,
    truncated: repairResponse.finishReason === 'MAX_TOKENS',
  });
}

export const executeInspectorConfirmedBeatRepair = async (params: {
  chapter: Chapter;
  beatRepairRequest: InspectorBeatRepairRequest;
  session: FourBeatChapterSession;
  storyBible: StoryBible;
  ledger: Ledger;
  plan?: ChapterPlan;
  softState?: SoftStateBrief | null;
  authoritativeContinuitySnapshot?: AuthoritativeContinuitySnapshot;
  onUserProgress?: (message: string) => void;
}): Promise<{ repairedBeat: import('../src/types').GeneratedChapterBeat }> => {
  const { chapter, beatRepairRequest, session, storyBible, ledger, plan, softState, authoritativeContinuitySnapshot } = params;
  const chapterNum = session.chapterNumber;
  const contract = session.contract;
  const contractBeat = contract.beats.find((beat) => beat.beatId === beatRepairRequest.beatId);
  if (!contractBeat) {
    throw new Error(`Unknown beat contract for ${beatRepairRequest.beatId}`);
  }

  const beats = splitAssembledChapterToBeats(chapter, contract);
  const deficientBeat = beats.find((beat) => beat.beatId === beatRepairRequest.beatId);
  if (!deficientBeat) {
    throw new Error(`Missing beat content for ${beatRepairRequest.beatId}`);
  }

  const classifiedBeat = buildInspectorClassifiedBeat(deficientBeat, contractBeat, beatRepairRequest);
  const previousAccepted = beats
    .filter((beat) => beat.sequence < contractBeat.sequence)
    .sort((a, b) => b.sequence - a.sequence)[0];
  const nextBeat = contract.beats.find((beat) => beat.sequence === contractBeat.sequence + 1);

  params.onUserProgress?.(`Repairing ${beatRepairRequest.beatId} after Inspector review...`);

  const safeLedger = getFilteredLedger(ledger, storyBible.canonId || 'fallback', storyBible.canonId || 'fallback');
  const planContext = plan ? `
    CHAPTER PLAN:
    TITLE: ${plan.title}
    SUMMARY: ${plan.summary}
    REQUIRED BEATS:
    ${(plan.requiredBeats || []).map((beat) => `- ${beat}`).join('\n    ')}
  ` : '';
  const softStateContext = softState ? `SOFT STATE:\n${JSON.stringify(softState)}` : '';
  const instruction = appendForbiddenProseInstruction(
    SYSTEM_INSTRUCTIONS.SCRIBE
      .replace('{{MODE}}', storyBible.mode)
      .replace('{{TARGET_COUNT}}', String(storyBible.targetChapterCount))
      .replace('{{LENGTH}}', storyBible.chapterLength)
      + `\nSTYLE GUIDE: ${storyBible.style_guide}`,
    storyBible,
  );

  const repairedBeat = await invokeTargetedFourBeatRepair({
    chapterNum,
    contract,
    repairRequest: {
      contract,
      contractBeat,
      deficientBeat,
      classifiedBeat,
      previousAcceptedEndingState: previousAccepted?.endingState,
      nextBeatOpeningState: nextBeat?.openingState,
      storyContext: '',
    },
    callBudget: session.callBudget,
    systemInstruction: instruction,
    model: WORKER_CONFIG.SCRIBE.model,
    planContext,
    softStateContext,
    storyBible,
    authoritativeContinuityContext: authoritativeContinuitySnapshot
      ? renderAuthoritativeContinuityPrompt(authoritativeContinuitySnapshot)
      : undefined,
  });

  console.log(
    `[FOUR BEAT INSPECTOR REPAIR] ${JSON.stringify({
      beatId: beatRepairRequest.beatId,
      reasonCodes: beatRepairRequest.reasonCodes,
      logicalCallsUsed: session.callBudget.logicalCallsUsed,
    })}`,
  );

  return { repairedBeat };
};

export const generateChapter = async (
  storyBible: StoryBible, 
  ledger: Ledger, 
  chapterNum: number, 
  prevSummary: string,
  correctionOrder?: string,
  editorFeedback?: string,
  directorsNote?: string,
  retryCount: number = 0,
  failedDraft?: string,
  softState?: SoftStateBrief | null,
  priorDelta?: NarrativeStateDelta,
  bookArcContract?: BookArcContract,
  progressionContract?: ChapterProgressionContract,
  options?: import('../src/types').FourBeatGenerationOptions,
): Promise<Chapter> => {
  const safeLedger = getFilteredLedger(ledger, storyBible.canonId || 'fallback', storyBible.canonId || 'fallback');
  const genreString = Array.isArray(storyBible.genre) ? storyBible.genre.map((g: any) => `${g.name} (${g.weight})`).join(', ') : (typeof storyBible.genre === 'string' ? storyBible.genre : '');
  const premiseString = `
    SURFACE: ${storyBible.premise.surface}
    TENSION: ${storyBible.premise.tension}
    CHANGE: ${storyBible.premise.change}
  `;
    const eraStateStr = formatEraState(storyBible.eraState);

  const closingInstructions = storyBible.isClosingMode 
    ? "CRITICAL: CLOSING PROTOCOL ACTIVE. You must begin resolving major plot threads. Drive the narrative toward a satisfying conclusion for this volume. Do not introduce new major mysteries." 
    : "";

  const chapterPlan = storyBible.chapterPlans?.find(p => p?.chapterNumber === chapterNum);
  const fourBeatContract = buildFourBeatChapterContract({
    plan: chapterPlan,
    chapterNumber: chapterNum,
    chapterLength: storyBible.chapterLength,
    ledger,
    prevSummary,
  });

  const emitUserProgress = (message: string) => options?.onUserProgress?.(message);
  const emitDeveloperDiagnostic = (diagnostic: import('../src/types').FourBeatGenerationDiagnostic) =>
    options?.onDeveloperDiagnostic?.(diagnostic);

  emitUserProgress('Planning four dramatic beats...');
  console.log(
    `[FOUR BEAT CONTRACT] ${JSON.stringify({
      chapterNumber: fourBeatContract.chapterNumber,
      chapterTitle: fourBeatContract.chapterTitle,
      minimumAcceptedWords: fourBeatContract.minimumAcceptedWords,
      preferredTargetWords: fourBeatContract.preferredTargetWords,
      beatFunctions: fourBeatContract.beats.map((beat) => beat.function),
      beatBudgets: fourBeatContract.beats.map((beat) => ({
        beatId: beat.beatId,
        targetMinWords: beat.targetMinWords,
        targetMaxWords: beat.targetMaxWords,
      })),
      roadmapBeatCount: fourBeatContract.requiredRoadmapBeats.length,
    })}`,
  );
  emitUserProgress('Writing four developed chapter beats...');

  const responseFormat = options?.responseFormat || 'FOUR_BEAT_V1';

  const proseControls = getProseControls(storyBible);
  const motionContracts = chapterPlan
    ? (chapterPlan.narrativeMotion?.length
        ? chapterPlan.narrativeMotion
        : buildNarrativeMotionContractsFromPlan(chapterPlan, chapterNum, proseControls))
    : [];
  const motionPrompt = motionContracts.length
    ? buildNarrativeMotionPrompt(motionContracts, chapterNum)
    : '';
  const proseControlPrompt = `${buildProseControlPrompt(proseControls)}\n${buildEffectiveProseInstruction(storyBible)}`;
  const wordCountPolicyPrompt = buildWordCountPolicyPrompt(proseControls);
  const planContext = chapterPlan ? `
    CHAPTER PLAN:
    CHAPTER NUMBER: ${chapterPlan.chapterNumber || chapterNum}
    TITLE: ${chapterPlan.title}
    CHAPTER TITLE: ${chapterPlan.chapterTitle || chapterPlan.title}
    SUMMARY: ${chapterPlan.summary}
    REQUIRED BEATS:
    ${(chapterPlan.requiredBeats || []).map(b => `- ${b}`).join('\n    ')}
    CHARACTER FOCUS: ${(chapterPlan.characterFocus || []).join(', ')}
    LOCATION FOCUS: ${(chapterPlan.locationFocus || []).join(', ')}
    CONTINUITY NOTES: ${(chapterPlan.continuityNotes || []).join(', ')}
    FORBIDDEN MOVES: ${(chapterPlan.forbiddenMoves || []).join(', ')}
  ` : "";
  const canonKnowledge = buildChapterCanonKnowledge({
    ledger: safeLedger,
    storyBible,
    plan: chapterPlan,
    chapterNumber: chapterNum,
  });
  const canonKnowledgePrompt = buildScribeCanonKnowledgeBlock(canonKnowledge);
  const physicalContinuityLookback = buildPhysicalContinuityLookbackSection(safeLedger, chapterPlan, chapterNum);
  const physicalContinuitySections = buildPhysicalContinuityContextSections(safeLedger, chapterPlan, chapterNum);
  const physicalContinuityScribeRule = buildPhysicalContinuityScribeRule();
  const authoritativeSnapshot = options?.authoritativeContinuitySnapshot;
  const authoritativeContinuityContext = authoritativeSnapshot
    ? renderAuthoritativeContinuityPrompt(authoritativeSnapshot)
    : '';
  const effectivePriorDelta =
    priorDelta || latestStateDeltaToNarrativeStateDelta(safeLedger.latestStateDelta);

  // Dynamic Percentage-Based Chapter Arc Stage Calculation
  const progressPercent = storyBible.targetChapterCount > 0 
    ? (chapterNum / storyBible.targetChapterCount) 
    : 0.5;

  let calculatedArcStage = "MIDPOINT_PIVOT";
  let dynamicArcObjective = "Advance key conflicts and deepen stakes.";
  
  if (progressPercent <= 0.12) {
    calculatedArcStage = "OPENING_SETUP";
    dynamicArcObjective = "Introduce core premise, establish protagonist baseline, and map scene locations.";
  } else if (progressPercent <= 0.25) {
    calculatedArcStage = "RISING_PRESSURE";
    dynamicArcObjective = "Introduce initial complications, heighten external stakes, and engage canon constraints.";
  } else if (progressPercent <= 0.45) {
    calculatedArcStage = "MIDPOINT_APPROACH";
    dynamicArcObjective = "Escalate external tension, show opposing factions gathering forces, and build toward midpoint pivot.";
  } else if (progressPercent <= 0.55) {
    calculatedArcStage = "MIDPOINT_PIVOT";
    dynamicArcObjective = "Introduce a key structural plot twist, pivot the protagonist's goals, and shift the emotional baseline.";
  } else if (progressPercent <= 0.70) {
    calculatedArcStage = "NARRATIVE_COLLAPSE";
    dynamicArcObjective = "The protagonist faces severe setbacks, internal or inventory losses, and plans unraveling.";
  } else if (progressPercent <= 0.85) {
    calculatedArcStage = "FINAL_PUSH";
    dynamicArcObjective = "Reassemble resources, deploy strategy, and escalate pacing dynamics.";
  } else if (progressPercent <= 0.95) {
    calculatedArcStage = "CLIMAX_PEAK";
    dynamicArcObjective = "Confront the main antagonist force, resolve primary premise conflict, and enforce canon locks.";
  } else {
    calculatedArcStage = "RESOLUTION";
    dynamicArcObjective = "Cool-down, stabilize characters, resolve minor sub-plots, and prepare seed hooks for sequel.";
  }

  const progressionContext = (effectivePriorDelta || progressionContract) ? `
    --- NARRATIVE PROGRESSION CONTRACT ---
    ${bookArcContract ? `BOOK ARC: ${bookArcContract.arcName}\nARC SUMMARY: ${bookArcContract.arcSummary}` : ''}
    
    ${progressionContract ? `CURRENT MILESTONE: ${progressionContract.roadmapMilestone}\nREQUIRED PRESSURE: ${progressionContract.requiredPressure}` : ''}
    
    ${effectivePriorDelta ? `STATE DELTA FROM PRIOR CHAPTER:
    - Fact Changes: ${effectivePriorDelta.fact_changes?.join('; ') || 'None'}
    - Emotional Shifts: ${effectivePriorDelta.emotional_shifts?.join('; ') || 'None'}
    - Unresolved Consequences: ${effectivePriorDelta.new_unresolved_consequences?.join('; ') || 'None'}
    - Resolved Consequences: ${effectivePriorDelta.resolved_consequences?.join('; ') || 'None'}
    ` : ''}
    --- END PROGRESSION CONTRACT ---
    
    CRITICAL PROGRESSION RULES:
    1. Acknowledge the Unresolved Consequences and Emotional Shifts from the Prior Chapter. Do not start this chapter in a vacuum.
    2. Move the story forward toward the CURRENT MILESTONE.
    3. Apply the REQUIRED PRESSURE.
  ` : "";

  const softStateContext = softState ? `
    SOFT STATE MEMORY (SUPPORTIVE ONLY):
    - Sensory Carryover: ${softState.recentSensoryCarryover}
    - Emotional Residue: ${softState.activeEmotionalResidue}
    - Voice Notes: ${softState.voiceNotes}
    - Environmental Cues: ${softState.environmentalContinuityCues}
    - Motifs: ${softState.motifsToLightlyPreserve}
    - ANTI-REPETITION: ${softState.antiRepetitionWarnings}
  ` : "";

  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => l.name);
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active') || activeLocations.includes(c.location)).map((c: any) => c.name);

  // 2. AMNESIA BUFFER: Explicitly filter the prompt
  const ledgerKnowledge = formatCanonEntriesForPrompt(ledger.resolved_plot_points)
    .split(', ')
    .filter((entry) => entry && entry !== 'None')
    .map((entry) => `[KNOWN: ${entry}]`)
    .join(' ');

  const prompt = `
    GENERATE CHAPTER ${chapterNum} of ${storyBible.targetChapterCount} [VOLUME ${storyBible.volume}].
    
    DYNAMIC NARRATIVE PACING DATA:
    - Progress Index: ${(progressPercent * 100).toFixed(1)}% of Dynamic Volume Chapters
    - Calculated Pacing Stage: ${calculatedArcStage}
    - Pacing Objective: ${dynamicArcObjective}
    
    CANON ANCHORS (SINGLE SOURCE OF TRUTH):
    - ACTIVE LOCATIONS: ${activeLocations.length > 0 ? activeLocations.join(', ') : "Unknown"}
    - ACTIVE CHARACTERS: ${activeCharacters.length > 0 ? activeCharacters.join(', ') : "Unknown"}
    - AVAILABLE CAST: ${Object.values(ledger.characters || {}).map((c: any) => c.name).join(', ')}
    - UNRESOLVED PLOT POINTS: ${formatCanonEntriesForPrompt(ledger.plot_points)}
    - RESOLVED PLOT POINTS (DO NOT REPEAT): ${formatCanonEntriesForPrompt(ledger.resolved_plot_points)}
    
    STRICT RULE: The characters are currently at the Active Locations. They ALREADY KNOW the Resolved Plot Points. Do NOT write them discovering, theorizing, or being shocked by these facts. Treat these facts as mundane background knowledge.
    
    STRICT HIERARCHY: Ledger > Plan > Style.
    Character Knowledge: ${ledgerKnowledge}
    DO NOT REPEAT RESOLVED POINTS.
    
    ${authoritativeContinuityContext
      ? `AUTHORITATIVE CONTINUITY SNAPSHOT (IDENTICAL FACTS FOR ALL AGENTS):\n${authoritativeContinuityContext}\n`
      : `CURRENT LEDGER: ${JSON.stringify(ledger)}`}
    PREVIOUS CONTEXT: ${prevSummary}
    
    ${planContext}

    ${authoritativeContinuityContext ? '' : `${canonKnowledgePrompt}

    ${physicalContinuitySections.combined ? `${physicalContinuitySections.combined}\n\n` : ''}${physicalContinuityScribeRule}`}

    CHAPTER CONTRACT (STRICT REQUIREMENTS):
    STRICT HIERARCHY OF TRUTH:
    1. MASTER LEDGER (Absolute Canon)
    2. DIRECTOR'S DIRECTION (Immediate Priority)
    3. CHAPTER PLAN (Required Beats)

    1. VALID PROGRESS: You must advance the 'KEY EVENTS' from the Chapter Plan. Do not stall in stasis.
       - [EXACT FACTS]: Must be executed literally, exactly as written. No deviation. Do not invent unlisted mechanics or tools.
       - [REQUIRED OUTCOMES]: The end result must happen, but you can invent the staging/action to get there.
       - [OPTIONAL STAGING]: Suggestions for action/dialogue. You can use them or invent your own if it fits better.
       - [EMOTIONAL PAYOFF ONLY]: Focus on the internal/emotional realization. The physical action doesn't matter as much.
       - [FORBIDDEN REPEAT]: CRITICAL! Do NOT write this happening. It is a trap. Do not summarize it. Do not mention it. It already happened.
    2. SCENE BOUNDARIES: Remain at the CURRENT LOCATION unless the Chapter Plan explicitly requires travel. No bad cutaways.
    3. CHARACTER STATE: Focus on the ACTIVE CHARACTERS and any characters specified in the CHAPTER PLAN's character focus. You may draw from the AVAILABLE CAST if necessary to execute the plan. Do NOT invent new main characters.
    4. ABILITIES & LORE: Strictly adhere to the rules established in the Ledger. No teleportation, no unearned powers, no invented bridge mechanics.
    5. BEAT CONSUMPTION: Do not repeat events from the PREVIOUS CONTEXT or RESOLVED PLOT POINTS.
    6. CAUSALITY: Actions must have logical consequences. Characters cannot know things they haven't learned.

    NARRATIVE DNA:
    GENRES: ${genreString}
    PREMISE: ${premiseString}

    ${softStateContext}
    ${getVolume6Lock(storyBible)}

    ${closingInstructions}
    ${directorsNote ? `DIRECTOR'S DIRECTION: ${directorsNote}` : ""}
    
    ${correctionOrder ? `
    ==================================================
    INSPECTOR'S CORRECTION ORDER (MANDATORY FIX):
    ${correctionOrder}
    
    ${failedDraft ? `PREVIOUS FAILED DRAFT (DO NOT REPEAT THESE MISTAKES):
    ${failedDraft}` : ""}
    
    CRITICAL: You are in a retry loop because your previous attempt failed validation. 
    You MUST apply the exact fixes requested in the Correction Order above. 
    IF THE CORRECTION ORDER CONFLICTS WITH THE CHAPTER PLAN, THE CORRECTION ORDER WINS.
    Do not repeat the same mistakes.
    ==================================================` : ""}
    
    ${editorFeedback ? `EDITOR NOTES: ${editorFeedback}` : ""}

    ${proseControlPrompt}

    ${motionPrompt}

    ${wordCountPolicyPrompt}

    ${buildWordCountTargetPrompt(resolveChapterWordTargets(storyBible.chapterLength))}

    ${buildFourBeatContractPrompt(fourBeatContract)}

    ORIGINAL PROVEN LENGTH DIRECTIVE (STANDARD chapters):
    - Aim for ${fourBeatContract.preferredTargetRangeMin}–${fourBeatContract.preferredTargetRangeMax} words total.
    - Hard minimum acceptable: ${fourBeatContract.minimumAcceptedWords} words.
    - Write at least ${fourBeatContract.beats[0].targetMinWords}–${fourBeatContract.beats[0].targetMaxWords} words in Beat 1,
      ${fourBeatContract.beats[1].targetMinWords}–${fourBeatContract.beats[1].targetMaxWords} in Beat 2,
      ${fourBeatContract.beats[2].targetMinWords}–${fourBeatContract.beats[2].targetMaxWords} in Beat 3,
      and ${fourBeatContract.beats[3].targetMinWords}–${fourBeatContract.beats[3].targetMaxWords} in Beat 4.
    - DO NOT summarize. DO NOT rush the ending. DO NOT deliver a short skeleton expecting later expansion.
    - Expand every beat with action, dialogue, reaction, and consequence — not inventory transcription.
    - Generate exactly 4 beats in the beats array with stable beatId values beat-1 through beat-4.
  `;

  const instruction = SYSTEM_INSTRUCTIONS.SCRIBE
    .replace('{{MODE}}', storyBible.mode)
    .replace('{{TARGET_COUNT}}', String(storyBible.targetChapterCount))
    .replace('{{LENGTH}}', storyBible.chapterLength) + 
    `\nSTYLE GUIDE: ${storyBible.style_guide}` +
    `\n${HUMAN_PROSE_CONSTRAINT}` +
    `\n${proseControlPrompt}` +
    (retryCount >= 2 && retryCount !== 4 ? "\n\nACT AS A DETERMINISTIC EXECUTION ENGINE. PRIORITIZE CONTINUITY OVER CREATIVE FLOURISH. IF A CHARACTER IS NOT AT THE CURRENT LOCATION IN THE LEDGER, THEY CANNOT APPEAR IN THIS CHAPTER." : "") +
    (retryCount >= 4 ? "\n\nCRITICAL DEADLOCK WARNING: You have failed to write this chapter multiple times. You MUST strictly adhere to the Ledger. Do NOT invent new mechanics, do NOT contradict resolved plot points, and do NOT teleport characters. Follow the Chapter Plan EXACTLY as written." : "");

  const model = WORKER_CONFIG.SCRIBE.model;
  const temperature = retryCount === 0 ? 1.0 : 
                      retryCount === 1 ? 0.7 : 
                      retryCount === 2 ? 0.6 : 
                      retryCount === 3 ? 0.5 : 
                      retryCount === 4 ? 0.7 : // Fresh start with new Architect plan
                      retryCount === 5 ? 0.6 : 
                      0.8;

  const scribeOperation = resolveScribeOperationForChapter(correctionOrder);
  const scribeThinking = resolveScribeThinkingConfig(scribeOperation);

  console.log(
    `[SCRIBE REQUEST] ${JSON.stringify({
      scribeOperation,
      thinkingLevel: scribeThinking.thinkingLevel,
      chapterNumber: chapterNum,
    })}`,
  );

  const callBudget = new FourBeatCallBudgetTracker(chapterNum);
  const initialLogicalCallId = buildLogicalFourBeatCallId({
    chapterNumber: chapterNum,
    scribeOperation,
    narrativeAttempt: 1,
  });

  const response = await generateFourBeatWithBoundedTransport(
    {
      model,
      contents: prompt,
      config: {
        systemInstruction: instruction,
        thinkingConfig: { thinkingLevel: scribeThinking.thinkingLevel },
        temperature,
        maxOutputTokens: 16384,
        responseMimeType: "application/json",
        responseSchema: fourBeatResponseSchema,
      },
    },
    {
      logicalCallId: initialLogicalCallId,
      chapterNumber: chapterNum,
      scribeOperation,
      narrativeAttempt: 1,
      generationPolicy: 'four_beat_initial',
      thinkingLevel: scribeThinking.thinkingLevel,
    },
    {
      callBudget,
      onDiagnostic: (diagnostic) =>
        console.log(`[FOUR BEAT TRANSPORT] ${JSON.stringify(diagnostic)}`),
    },
  );

  const json = extractAndParseJSON(response.text || "");

  if (responseFormat === 'LEGACY_SCENES_IMPORT') {
    const legacyGenerated = parseLegacyScenesImport(chapterNum, json, {
      finishReason: response.finishReason,
      outputTokenCount: response.outputTokenCount,
    });
    emitUserProgress(`Four-beat chapter assembled: ${legacyGenerated.totalWordCount} words.`);
    return {
      id: chapterNum,
      title: legacyGenerated.title,
      summary: legacyGenerated.summary,
      content: legacyGenerated.content,
    };
  }

  const gateResult = await executeFourBeatGenerationGate({
    chapterId: chapterNum,
    contract: fourBeatContract,
    title: json.title || chapterPlan?.title || `Chapter ${chapterNum}`,
    summary: json.summary || '',
    initialJson: json,
    responseMeta: {
      finishReason: response.finishReason,
      outputTokenCount: response.outputTokenCount,
    },
    onDeveloperLog: (tag, payload) => console.log(`${tag} ${JSON.stringify(payload)}`),
    onUserProgress: emitUserProgress,
    repairBeat: async (repairRequest) => {
      const repairedBeat = await invokeTargetedFourBeatRepair({
        chapterNum,
        contract: fourBeatContract,
        repairRequest,
        callBudget,
        systemInstruction: instruction,
        model,
        planContext,
        softStateContext: softState ? `SOFT STATE:\n${JSON.stringify(softState)}` : '',
        storyBible,
        authoritativeContinuityContext: authoritativeContinuityContext || undefined,
      });
      return { beat: repairedBeat };
    },
  });

  const generated = gateResult.generated;
  const gate = gateResult.gate;

  const diagnostic: import('../src/types').FourBeatGenerationDiagnostic = {
    scribeOperation,
    thinkingLevel: scribeThinking.thinkingLevel,
    chapterNumber: chapterNum,
    beatCount: generated.beats.length,
    beatBudgets: fourBeatContract.beats.map((beat) => ({
      beatId: beat.beatId,
      targetMinWords: beat.targetMinWords,
      targetMaxWords: beat.targetMaxWords,
    })),
    beatWordCounts: generated.beats.map((beat) => beat.wordCount),
    totalWordCount: generated.totalWordCount,
    finishReason: response.finishReason,
    outputTokenCount: response.outputTokenCount,
    truncated: generated.beats.some((beat) => beat.truncated),
    validationIssues: gate.issues,
  };
  emitDeveloperDiagnostic(diagnostic);

  const session: FourBeatChapterSession = {
    projectId: options?.fourBeatRunContext?.projectId || '',
    chapterNumber: chapterNum,
    runId: options?.fourBeatRunContext?.runId || '',
    callBudget,
    contract: fourBeatContract,
    preInspectorRepairedBeatIds: gate.preInspectorRepairedBeatIds || [],
    postInspectorRepairedBeatIds: [],
    inspectorCallsUsed: 0,
  };
  registerFourBeatChapterSession(session);
  options?.onFourBeatSession?.(session);

  const cleanedContent = await finalizeProseOutput(generated.content, storyBible, 'scribe_four_beat_initial');

  return {
    id: chapterNum,
    title: generated.title,
    summary: generated.summary,
    content: cleanedContent,
    fourBeatProvenance: {
      responseFormat: 'FOUR_BEAT_V1',
      generationArchitecture: 'FOUR_BEAT',
    },
  };
};

export interface ExpandChapterResult {
  chapter: Chapter;
  passResult: ExpansionPassResult;
  metadata: ExpansionOutputMetadata;
}

const buildExpansionSharedContext = (
  storyBible: StoryBible,
  plan: ChapterPlan | undefined,
  warnings: string[],
  softState?: SoftStateBrief | null,
) => {
  const planContext = plan ? `
    CHAPTER PLAN:
    TITLE: ${plan.title}
    CHAPTER TITLE: ${(plan as any).chapterTitle || plan.title}
    POV: ${plan.pov || 'Omniscient'}
    LOCATION: ${plan.locationFocus?.join(', ') || 'Current location'}
    SUMMARY: ${plan.summary}
    REQUIRED BEATS:
    ${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n    ')}
    CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
    LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
  ` : '';

  const warningsContext = warnings.length > 0
    ? `ACCEPTANCE GATE WARNINGS / WEAK BEATS (MUST RESOLVE/EXPAND UPON):
    ${warnings.map(w => `- ${w}`).join('\n    ')}`
    : 'None.';

  const softStateContext = softState ? `
    DETERMINISTIC LOOKBACK CONTEXT & CANON RESTRICTIONS:
    - Sensory & Lore: ${softState.recentSensoryCarryover}
    - Active State & Cast: ${softState.activeEmotionalResidue}
    - Global Lore: ${softState.voiceNotes}
    - Chapter Outline: ${softState.environmentalContinuityCues}
    - Canon Locks & Excluded Cast: ${softState.antiRepetitionWarnings}
  ` : '';

  return { planContext, warningsContext, softStateContext };
};

const buildExpansionInstruction = (storyBible: StoryBible, proseControls: ReturnType<typeof getProseControls>) =>
  SYSTEM_INSTRUCTIONS.SCRIBE
    .replace('{{MODE}}', storyBible.mode)
    .replace('{{TARGET_COUNT}}', String(storyBible.targetChapterCount))
    .replace('{{LENGTH}}', storyBible.chapterLength) +
  `\nSTYLE GUIDE: ${storyBible.style_guide}` +
  `\n${HUMAN_PROSE_CONSTRAINT}` +
  `\n${buildProseControlPrompt(proseControls)}` +
  `\n${buildEffectiveProseInstruction(storyBible)}`;

const evaluateExpansionQuality = (
  prose: string,
  chapterNum: number,
  plan: ChapterPlan | undefined,
  proseControls: ReturnType<typeof getProseControls>,
) => {
  const momentumEval = evaluateChapterMomentum(prose, chapterNum, plan, proseControls);
  return {
    momentumPasses: !momentumEval.shouldBlock,
    paddingViolation: momentumEval.shouldBlockRepetition,
  };
};

const finalizeExpandChapterResult = (
  chapter: Chapter,
  previousWordCount: number,
  expansionPlan: ChapterExpansionPlan,
  responseMeta: { finishReason?: string; outputTokenCount?: number },
  model: string,
  chapterNum: number,
  plan: ChapterPlan | undefined,
  proseControls: ReturnType<typeof getProseControls>,
): ExpandChapterResult => {
  const newWordCount = countChapterWords(chapter.content || '');
  const metadata = buildExpansionOutputMetadata({
    model,
    requestedTargetWords: expansionPlan.minimumAcceptedWords,
    outputWords: newWordCount,
    finishReason: responseMeta.finishReason,
    outputTokenCount: responseMeta.outputTokenCount,
  });
  const quality = evaluateExpansionQuality(chapter.content || '', chapterNum, plan, proseControls);
  const passResult = evaluateExpansionPassResult({
    previousWordCount,
    newWordCount,
    minimumUsefulGrowth: expansionPlan.minimumUsefulGrowth,
    metadata,
    momentumPasses: quality.momentumPasses,
    paddingViolation: quality.paddingViolation,
  });
  return { chapter, passResult, metadata };
};

export interface ExpandChapterOptions {
  onUserProgress?: (message: string) => void;
  onDeveloperDiagnostic?: (diagnostic: ScribeSceneDiagnostic) => void;
}

export const expandChapter = async (
  storyBible: StoryBible,
  ledger: Ledger,
  chapterNum: number,
  draftContent: string,
  warnings: string[],
  plan?: ChapterPlan,
  softState?: SoftStateBrief | null,
  expansionPlanInput?: ChapterExpansionPlan,
  options?: ExpandChapterOptions,
): Promise<ExpandChapterResult> => {
  const proseControls = getProseControls(storyBible);
  const expansionGuard = buildExpansionMomentumGuard(proseControls);
  const wordCountPolicyPrompt = buildWordCountPolicyPrompt(proseControls);
  const dispatch = resolveMajorExpansionDispatch(
    expansionPlanInput,
    draftContent,
    plan,
    storyBible.chapterLength,
  );
  const expansionPlan = dispatch.expansionPlan;
  const previousWordCount = expansionPlan.currentWordCount;
  const { planContext, warningsContext, softStateContext } = buildExpansionSharedContext(storyBible, plan, warnings, softState);
  const targets = resolveChapterWordTargets(storyBible.chapterLength);
  const model = WORKER_CONFIG.SCRIBE.model;

  const emitUserProgress = (message: string) => options?.onUserProgress?.(message);
  const emitDiagnostic = (diagnostic: ScribeSceneDiagnostic) => options?.onDeveloperDiagnostic?.(diagnostic);

  if (dispatch.ignoredInvalidInput) {
    console.warn(
      `[MAJOR DISPATCH FALLBACK] reason=invalid_expansion_plan_input rebuilt_from_draft chapterNumber=${chapterNum}`,
    );
  }
  if (dispatch.planStrategyMismatch) {
    console.warn(
      `[MAJOR DISPATCH FALLBACK] reason=plan_strategy_mismatch authoritative=${dispatch.authoritativeStrategy} chapterNumber=${chapterNum}`,
    );
  }

  // Phase 1 one-call MAJOR rebuild removed — MAJOR always uses scene-budgeted Phase 1B below.
  if (dispatch.dispatchStrategy === 'MAJOR') {
    console.log(
      `[MAJOR DISPATCH] implementation=SCENE_BUDGETED_PHASE_1B ${JSON.stringify({
        expansionStrategy: 'MAJOR',
        implementation: 'SCENE_BUDGETED_PHASE_1B',
        inputWordCount: previousWordCount,
        minimumAcceptedWords: expansionPlan.minimumAcceptedWords,
        chapterNumber: chapterNum,
      })}`,
    );
    const majorPlan = buildMajorChapterBuildPlan({
      draftContent,
      plan,
      chapterNumber: chapterNum,
      chapterLength: storyBible.chapterLength,
    });

    const preservedLines = [
      ...majorPlan.preservedSourceMaterial.canonEvents.map((e) => `- event: ${e}`),
      ...majorPlan.preservedSourceMaterial.usefulDialogue.map((d) => `- dialogue: ${d}`),
      ...majorPlan.preservedSourceMaterial.decisions.map((d) => `- decision: ${d}`),
      ...majorPlan.preservedSourceMaterial.discoveries.map((d) => `- discovery: ${d}`),
    ].join('\n');

    const buildScenePrompt = (
      scene: typeof majorPlan.scenes[number],
      ctx: {
        previousEndingState?: string;
        isRetry?: boolean;
        retryReasons?: string[];
        gapFillWords?: number;
      },
    ) => {
      const preservedForScene = scene.preservedMaterialRefs.map((ref) => `- ${ref}`).join('\n');
      const retryBlock = ctx.isRetry
        ? `\nRETRY CORRECTION:\n${(ctx.retryReasons || []).map((r) => `- ${r}`).join('\n')}\nRewrite ${scene.sceneId} only. Do not regenerate other scenes.`
        : '';
      const gapBlock = ctx.gapFillWords
        ? `\nTARGETED GAP FILL: Add approximately ${ctx.gapFillWords} words of new story motion to this scene only.`
        : '';

      return `
You are writing ONE scene for a MAJOR chapter rebuild. Return prose for ${scene.sceneId} only.

SCENE SPEC:
- sceneId: ${scene.sceneId}
- sequence: ${scene.sequence} of ${majorPlan.scenes.length}
- POV: ${scene.povCharacter}
- location: ${scene.location}
- openingState: ${ctx.previousEndingState || scene.openingState}
- dramaticGoal: ${scene.dramaticGoal}
- obstacle: ${scene.obstacle}
- requiredDecision: ${scene.decision}
- requiredConsequence: ${scene.consequence}
- requiredBeats: ${scene.requiredBeats.map((b) => `"${b}"`).join(', ') || 'none'}
- targetMinWords: ${scene.targetMinWords}
- targetMaxWords: ${scene.targetMaxWords}
- acceptanceFloorWords: ${scene.acceptanceFloorWords}
- continuityInputs: ${scene.continuityInputs.join('; ') || 'none'}
- forbiddenPadding: ${scene.forbiddenPadding.join('; ')}

PRESERVED SOURCE MATERIAL FOR THIS SCENE (must not lose valid substance):
${preservedForScene || 'none assigned'}

CHAPTER-LEVEL PRESERVED MATERIAL:
${preservedLines || 'none'}

${planContext}
${warningsContext}
${softStateContext}
${getVolume6Lock(storyBible)}

${expansionGuard}
${wordCountPolicyPrompt}
${buildWordCountTargetPrompt(targets)}

ALLOWED DRAMATIC METHODS: ${formatMajorBuildAllowedMethods()}
${retryBlock}
${gapBlock}

Write complete scene prose meeting the word minimum. Include a visible decision and consequence.
`.trim();
    };

    const majorBuild = await executeMajorChapterBuild({
      plan: majorPlan,
      title: plan?.title || `Chapter ${chapterNum}`,
      summary: plan?.summary || '',
      callbacks: {
        onProgress: (event: MajorBuildProgressEvent) => emitUserProgress(event.message),
      },
      generateScene: async ({
        scene,
        previousEndingState,
        isRetry,
        isWeakestRegeneration,
        retryReasons,
        logicalCallNumber,
        logicalSceneCallId,
        attemptNumber,
        gapFillWords,
      }) => {
        const majorSceneOperation: MajorSceneOperation = resolveMajorSceneOperation({
          gapFillWords,
          isRetry,
          isWeakestRegeneration,
        });
        const scribeOperation = resolveScribeOperationForMajorScene(majorSceneOperation);
        const scribeThinking = resolveScribeThinkingConfig(scribeOperation);
        const prompt = buildScenePrompt(scene, {
          previousEndingState,
          isRetry,
          retryReasons,
          gapFillWords,
        });

        const response = await generateMajorSceneWithBoundedTransport(
          {
            model,
            contents: prompt,
            config: {
              systemInstruction: buildExpansionInstruction(storyBible, proseControls) +
                '\nACT AS A SCENE-BUDGETED MAJOR REBUILD ENGINE. ONE SCENE ONLY. PRESERVE CANON OUTCOMES.',
              thinkingConfig: { thinkingLevel: scribeThinking.thinkingLevel },
              temperature: isRetry ? 0.65 : 0.7,
              maxOutputTokens: gapFillWords ? 4096 : 8192,
              responseMimeType: 'application/json',
              responseSchema: scribeSceneResponseSchema,
            },
          },
          {
            logicalSceneCallId,
            sceneId: scene.sceneId,
            sceneAttempt: attemptNumber,
            operation: majorSceneOperation,
          },
          {
            onDiagnostic: (transportDiagnostic) => {
              emitDiagnostic({
                scribeOperation,
                thinkingLevel: scribeThinking.thinkingLevel,
                sceneId: scene.sceneId,
                targetMinWords: scene.targetMinWords,
                targetMaxWords: scene.targetMaxWords,
                acceptanceFloorWords: scene.acceptanceFloorWords,
                attemptNumber,
                logicalCallNumber,
                logicalSceneCallId: transportDiagnostic.logicalSceneCallId,
                sceneAttempt: transportDiagnostic.sceneAttempt,
                transportAttempt: transportDiagnostic.transportAttempt,
                serverUpstreamAttempt: transportDiagnostic.serverUpstreamAttempt,
                generationPolicy: transportDiagnostic.generationPolicy,
              });
            },
          },
        );

        const json = extractAndParseJSON(response.text || '');
        const generated = toGeneratedExpansionScene(scene.sceneId, {
          content: json.content || '',
          summary: json.summary || '',
          endingState: json.endingState || '',
          requiredBeatsSatisfied: json.requiredBeatsSatisfied || [],
          decisionPresent: Boolean(json.decisionPresent),
          consequencePresent: Boolean(json.consequencePresent),
          finishReason: response.finishReason,
          outputTokenCount: response.outputTokenCount,
          truncated: detectOutputTruncation({
            finishReason: response.finishReason,
            outputWords: countChapterWords(json.content || ''),
            requestedTargetWords: scene.acceptanceFloorWords,
          }),
        });

        emitDiagnostic({
          scribeOperation,
          thinkingLevel: scribeThinking.thinkingLevel,
          sceneId: scene.sceneId,
          targetMinWords: scene.targetMinWords,
          targetMaxWords: scene.targetMaxWords,
          acceptanceFloorWords: scene.acceptanceFloorWords,
          outputWords: generated.wordCount,
          requiredBeatsSatisfied: generated.requiredBeatsSatisfied,
          finishReason: generated.finishReason,
          outputTokenCount: generated.outputTokenCount,
          truncated: generated.truncated,
          attemptNumber,
          logicalCallNumber,
          logicalSceneCallId,
          sceneAttempt: attemptNumber,
          transportAttempt: response.transportAttemptsUsed,
          serverUpstreamAttempt: 1,
          generationPolicy: 'major_scene',
        });

        return generated;
      },
    });

    const chapter: Chapter = {
      id: chapterNum,
      title: majorBuild.title,
      summary: majorBuild.summary,
      content: await finalizeProseOutput(majorBuild.content, storyBible, 'phase1b_major'),
    };

    const metadata = buildExpansionOutputMetadata({
      model,
      requestedTargetWords: expansionPlan.minimumAcceptedWords,
      outputWords: majorBuild.assembledWordCount,
      finishReason: majorBuild.belowMinimumAfterRepair ? 'STOP' : 'STOP',
      outputTokenCount: undefined,
    });

    const quality = evaluateExpansionQuality(chapter.content || '', chapterNum, plan, proseControls);
    const passResult = evaluateExpansionPassResult({
      previousWordCount,
      newWordCount: majorBuild.assembledWordCount,
      minimumUsefulGrowth: expansionPlan.minimumUsefulGrowth,
      metadata,
      momentumPasses: quality.momentumPasses,
      paddingViolation: quality.paddingViolation,
    });

    if (majorBuild.belowMinimumAfterRepair && passResult.status === 'SUCCESS') {
      passResult.status = 'EXPANSION_INSUFFICIENT_GROWTH';
      passResult.reason = `Assembled ${majorBuild.assembledWordCount} words; need at least ${expansionPlan.minimumAcceptedWords}.`;
    }

    return { chapter, passResult, metadata };
  }

  if (dispatch.authoritativeStrategy === 'MAJOR') {
    throw new Error(
      `[MAJOR DISPATCH] authoritative MAJOR deficit reached MINOR path — routing defect (chapter ${chapterNum}).`,
    );
  }

  const scenes = splitChapterIntoScenes(draftContent);
  const scenePayload = scenes.map((scene) => ({
    sceneId: scene.sceneId,
    text: scene.text,
    allocation: expansionPlan.sceneExpansions.find((item) => item.sceneId === scene.sceneId),
  }));

  const prompt = `
You are the Lead Expansion Editor performing TARGETED SCENE INSERTIONS.

CRITICAL: Do NOT rewrite or replace the existing chapter. Return only NEW passages to insert.
Preserve every word of the base draft. Your insertions will be merged at explicit anchors.

BASE DRAFT SCENES (PRESERVE EXACTLY — DO NOT RETURN REWRITTEN SCENES):
${scenePayload.map((scene) => `### ${scene.sceneId}\n${scene.text}`).join('\n\n')}

${planContext}
${warningsContext}
${softStateContext}
${getVolume6Lock(storyBible)}

${buildEffectiveProseInstruction(storyBible)}

${buildExpansionPlanPrompt(expansionPlan)}
${expansionGuard}
${wordCountPolicyPrompt}

For each scene allocation, return one or more insertions with:
- sceneId
- afterAnchor (exact substring from that scene — use the provided anchor when possible)
- passage (new prose only)
- narrativeFunction (what story job this passage performs)

Total new prose across all insertions should be at least ${expansionPlan.minimumUsefulGrowth} words.
`;

  const minorThinking = resolveScribeThinkingConfig('MINOR_INSERTION');

  const response = await generateWithRetry({
    model,
    contents: prompt,
    config: {
      systemInstruction: buildExpansionInstruction(storyBible, proseControls) +
        '\nACT AS A SCENE INSERTION ENGINE. RETURN ONLY NEW PASSAGES — NEVER REPLACE EXISTING PROSE.',
      thinkingConfig: { thinkingLevel: minorThinking.thinkingLevel },
      temperature: 0.65,
      maxOutputTokens: 16384,
      responseMimeType: 'application/json',
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          summary: { type: Type.STRING },
          insertions: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                sceneId: { type: Type.STRING },
                afterAnchor: { type: Type.STRING },
                passage: { type: Type.STRING },
                narrativeFunction: { type: Type.STRING },
              },
              required: ['sceneId', 'passage', 'narrativeFunction'],
            },
          },
        },
        required: ['title', 'insertions', 'summary'],
      },
    },
  }, 240000, {
    operation: 'MINOR_INSERTION',
    agent: 'scribe',
    maxRetries: minorThinking.transportMaxRetries,
  });

  emitDiagnostic({
    scribeOperation: 'MINOR_INSERTION',
    thinkingLevel: minorThinking.thinkingLevel,
    outputWords: undefined,
    attemptNumber: 1,
  });

  const json = extractAndParseJSON(response.text || '');
  const insertions = (json.insertions || []) as SceneExpansionInsertion[];
  const mergedContent = insertions.length > 0
    ? applySceneInsertions(scenes, insertions)
    : draftContent;

  const chapter: Chapter = {
    id: chapterNum,
    title: json.title || plan?.title || `Chapter ${chapterNum}`,
    summary: json.summary || plan?.summary || '',
    content: await finalizeProseOutput(mergedContent, storyBible, 'phase1b_minor'),
  };

  return finalizeExpandChapterResult(
    chapter,
    previousWordCount,
    expansionPlan,
    { finishReason: response.finishReason, outputTokenCount: response.outputTokenCount },
    model,
    chapterNum,
    plan,
    proseControls,
  );
};

export const editChapter = async (chapter: Chapter, bible: StoryBible, ledger: Ledger, plan?: ChapterPlan): Promise<EditorResult> => {
  const safeLedger = getFilteredLedger(ledger, bible.canonId || 'fallback', bible.canonId || 'fallback');
  const instruction = appendForbiddenProseInstruction(
    SYSTEM_INSTRUCTIONS.EDITOR.replace('{{MODE}}', bible.mode) + `\nStyle Guide: ${bible.style_guide}\n${HUMAN_PROSE_CONSTRAINT}`,
    bible,
  );
  const planContext = plan ? `CHAPTER PLAN:
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}` : "None";

  const response = await generateWithRetry({
    model: WORKER_CONFIG.EDITOR.model,
    contents: `CURRENT LEDGER:\n${JSON.stringify(safeLedger)}\n\n${planContext}\n\n${getVolume6Lock(bible)}\n\nCritique and polish this content:\n\n${chapter.content}`,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.EDITOR.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          feedback: { type: Type.STRING },
          polished_scenes: {
            type: Type.ARRAY,
            description: "The polished scenes. Maintain the length of the original scenes. Do not summarize or shorten them.",
            items: { type: Type.STRING }
          }
        },
        required: ["status", "feedback", "polished_scenes"]
      }
    },
  });
  const res = extractAndParseJSON(response.text || "");
  
  let polishedText = res.polished_text;
  if (res.polished_scenes && Array.isArray(res.polished_scenes)) {
    polishedText = res.polished_scenes.join('\n\n***\n\n');
  }

  const cleanedText = await finalizeProseOutput(polishedText || chapter.content, bible, 'editor');

  return {
    status: res.status || 'APPROVED',
    feedback: res.feedback || 'No specific feedback provided.',
    polished_text: cleanedText
  };
};

export const replanCurrentChapter = async (
  currentPlan: ChapterPlan,
  ledger: Ledger,
  inspectorFeedback: string,
  previousContext: string
): Promise<ChapterPlan> => {
  const prompt = `You are the Lead Producer. The current Chapter Plan is causing a logic error (Conflict: ${inspectorFeedback}). 
Rewrite the Chapter Plan to remove the conflict while preserving the Ledger's Absolute Canon. Ensure the characters act on their existing knowledge.

<ORIGINAL_PLAN>
${JSON.stringify(currentPlan, null, 2)}
</ORIGINAL_PLAN>

<CURRENT_LEDGER>
${JSON.stringify(ledger, null, 2)}
</CURRENT_LEDGER>

<PREVIOUS_CHAPTER_SUMMARY>
${previousContext}
</PREVIOUS_CHAPTER_SUMMARY>

Your task: REWRITE the chapter plan for this single chapter.
The new plan MUST:
1. Strictly respect the Current Ledger.
2. Directly resolve the Inspector's feedback.
3. Provide a logical, achievable bridge from the Previous Chapter Summary.
4. Abandon any plot points from the Original Plan that are currently impossible.
5. If the Ledger has Unresolved Plot Points that MUST happen before the Original Plan can occur, you MUST make those Unresolved Plot Points the focus of this new Chapter Plan.

Return a JSON object matching the ChapterPlan interface:
{
  "chapterNumber": number,
  "title": string,
  "pov": string,
  "summary": string,
  "requiredBeats": string[],
  "forbiddenMoves": string[],
  "continuityNotes": string[],
  "characterFocus": string[],
  "locationFocus": string[]
}
Return ONLY the JSON object. Do not include commentary.`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.CHAPTER_CONTRACT.model,
    contents: prompt,
    config: { 
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHITECT,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.CHAPTER_CONTRACT.thinkingLevel },
      temperature: 0.7,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          chapterNumber: { type: Type.NUMBER },
          title: { type: Type.STRING },
          pov: { type: Type.STRING },
          summary: { type: Type.STRING },
          requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
          forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
          continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
          characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
          locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["chapterNumber", "title", "summary", "requiredBeats"]
      }
    }
  });

  let parsed = extractAndParseJSON(response.text || "{}");
  if (parsed.chapterPlan) parsed = parsed.chapterPlan;
  if (parsed.plan) parsed = parsed.plan;
  
  return {
    ...currentPlan,
    ...parsed
  };
};

// ── repairChapterPlanAgainstCanon ────────────────────────────────────────────
// Pre-Scribe validation and targeted repair of a chapter plan.
// Detects beats that contradict EraState / Ledger / character canon locks,
// then rewrites ONLY the offending beats to canon-compliant equivalents.
// Returns { repairedPlan, wasRepaired, conflicts }.
export const repairChapterPlanAgainstCanon = async (
  chapterPlan: ChapterPlan,
  ledger: Ledger,
  bible: StoryBible,
  previousContext: string,
  authoritativeContinuitySnapshot?: AuthoritativeContinuitySnapshot,
): Promise<{ repairedPlan: ChapterPlan; wasRepaired: boolean; conflicts: string[] }> => {
  const eraStateStr = formatEraState(bible.eraState);
  const historicalEvents = Array.isArray((ledger as any).historical_events)
    ? formatCanonEntriesMultiline((ledger as any).historical_events, 'ledger.historical_events')
    : '';
  const resolvedPoints = formatCanonEntriesMultiline(
    ledger.resolved_plot_points,
    'ledger.resolved_plot_points',
  );
  const canonLocks = formatCanonLocksForPrompt(
    (ledger as any).canonLocks || (bible as any).canonLocks,
    'storyBible.canonLocks',
  );
  const charLocks = Object.values(ledger.characters || {})
    .filter((c: any) => c?.canon_locks || c?.canonLocks)
    .map((c: any) => {
      const locks = c.canon_locks || c.canonLocks;
      const lockText = Array.isArray(locks)
        ? formatCanonEntriesForPrompt(locks, { sourceField: 'character.canonLocks', emptyLabel: '' })
        : formatCanonLocksForPrompt(locks, 'character.canonLocks');
      return `${c.name || ''}: ${lockText}`;
    })
    .join('\n');

  const prompt = `You are the CONTINUITY INSPECTOR and PLAN SURGEON.
Your task has TWO phases:
PHASE 1 — DETECT: Identify every beat or claim in the Chapter Plan that contradicts the current canon state.
PHASE 2 — REPAIR: Rewrite ONLY the offending beats to be canon-compliant. Preserve all non-conflicting beats exactly.

<ERA_STATE>
${eraStateStr}
</ERA_STATE>

<LEDGER_CANON_LOCKS>
${canonLocks}
</LEDGER_CANON_LOCKS>

<CHARACTER_CANON_LOCKS>
${charLocks}
</CHARACTER_CANON_LOCKS>

<HISTORICAL_EVENTS>
- ${historicalEvents}
</HISTORICAL_EVENTS>

<RESOLVED_PLOT_POINTS>
- ${resolvedPoints}
</RESOLVED_PLOT_POINTS>

<PREVIOUS_CHAPTER_SUMMARY>
${previousContext}
</PREVIOUS_CHAPTER_SUMMARY>

${authoritativeContinuitySnapshot ? `<AUTHORITATIVE_CONTINUITY_SNAPSHOT>\n${authoritativeContinuitySnapshot.promptText}\n</AUTHORITATIVE_CONTINUITY_SNAPSHOT>` : ''}

<CHAPTER_PLAN_TO_VALIDATE>
${JSON.stringify(chapterPlan, null, 2)}
</CHAPTER_PLAN_TO_VALIDATE>

REPAIR RULES:
1. If a beat references a character as living/active when the ledger marks them dead/neutralized, rewrite the beat to reference their legacy, records, faction remnants, or ideological influence.
2. If a beat uses an old-era system as currently active, rewrite it as a historical echo, memory, archival record, or resonance artifact.
3. If a beat requires a character's physical presence when their state is spectral/anchor/non-corporeal, rewrite it as their equivalent spectral-state action.
4. Never remove beats — find a canon-compliant equivalent preserving narrative function.
5. If NO beats conflict, return wasRepaired=false and repairedPlan unchanged.

Return a JSON object.`;

  let result: any;
  try {
    const response = await generateWithRetry({
      model: WORKER_CONFIG.CHAPTER_CONTRACT.model,
      contents: prompt,
      config: {
        systemInstruction: SYSTEM_INSTRUCTIONS.ARCHITECT + "\nYou are performing pre-flight canon validation. Detect real contradictions only — do not flag story progression that is consistent with the current EraState.",
        thinkingConfig: { thinkingLevel: WORKER_CONFIG.CHAPTER_CONTRACT.thinkingLevel },
        temperature: 0.5,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            wasRepaired: { type: Type.BOOLEAN },
            conflicts: { type: Type.ARRAY, items: { type: Type.STRING } },
            repairedPlan: {
              type: Type.OBJECT,
              properties: {
                chapterNumber: { type: Type.NUMBER },
                title: { type: Type.STRING },
                pov: { type: Type.STRING },
                summary: { type: Type.STRING },
                requiredBeats: { type: Type.ARRAY, items: { type: Type.STRING } },
                forbiddenMoves: { type: Type.ARRAY, items: { type: Type.STRING } },
                continuityNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                characterFocus: { type: Type.ARRAY, items: { type: Type.STRING } },
                locationFocus: { type: Type.ARRAY, items: { type: Type.STRING } }
              },
              required: ["chapterNumber", "title", "summary", "requiredBeats"]
            }
          },
          required: ["wasRepaired", "conflicts", "repairedPlan"]
        }
      }
    });
    result = extractAndParseJSON(response.text || "{}");
  } catch {
    // On any failure, return original plan as safe fallback
    return { repairedPlan: chapterPlan, wasRepaired: false, conflicts: [] };
  }

  const repairedPlan: ChapterPlan = {
    ...chapterPlan,
    ...(result.repairedPlan || {}),
    chapterNumber: chapterPlan.chapterNumber,
    id: chapterPlan.id,
    status: chapterPlan.status,
    updatedAt: new Date().toISOString()
  };

  return {
    repairedPlan,
    wasRepaired: result.wasRepaired === true,
    conflicts: Array.isArray(result.conflicts) ? result.conflicts : []
  };
};

export const generateDirectorsNote = async (
  failedDraft: string,
  inspectorFeedback: string,
  ledger: Ledger,
  plan: ChapterPlan
): Promise<string> => {
  const prompt = `You are the AI Director. The Scribe has repeatedly failed to write this chapter.
The Inspector rejected the last draft with this feedback:
<INSPECTOR_FEEDBACK>
${inspectorFeedback}
</INSPECTOR_FEEDBACK>

<CHAPTER_PLAN>
${JSON.stringify(plan, null, 2)}
</CHAPTER_PLAN>

<CURRENT_LEDGER>
${JSON.stringify(ledger, null, 2)}
</CURRENT_LEDGER>

Your task is to write a short, punchy "Director's Note" to the Scribe for their next attempt.
This note must explicitly tell the Scribe what to focus on and what to avoid to satisfy the Inspector.
Example: "The Inspector says Jorek already knows about the sacrifices. Next attempt: Focus ONLY on the tension of the stealth walk. Do not mention the machine's purpose."

Return ONLY the text of the Director's Note.`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.DIRECTOR_REWRITE.model,
    contents: prompt,
    config: {
      temperature: 0.7,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.DIRECTOR_REWRITE.thinkingLevel }
    }
  });

  return response.text?.trim() || "Follow the Inspector's feedback strictly.";
};

export const rewriteDeadlockedDraft = async (
  failedDraft: string,
  inspectorFeedback: string,
  ledger: Ledger,
  plan: ChapterPlan,
  previousContext: string,
  bible: StoryBible,
  softState?: SoftStateBrief | null
): Promise<Chapter> => {
  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => l.name);
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active') || activeLocations.includes(c.location)).map((c: any) => c.name);

  const softStateContext = softState ? `
<SOFT_STATE_MEMORY>
- Sensory Carryover: ${softState.recentSensoryCarryover}
- Emotional Residue: ${softState.activeEmotionalResidue}
- Voice Notes: ${softState.voiceNotes}
- Environmental Cues: ${softState.environmentalContinuityCues}
- Motifs: ${softState.motifsToLightlyPreserve}
- ANTI-REPETITION: ${softState.antiRepetitionWarnings}
</SOFT_STATE_MEMORY>` : "";

  const prompt = `You are the AI DIRECTOR, stepping in as the human author to fix a critically deadlocked chapter.
The Scribe AI has failed multiple times to write this chapter without breaking continuity.
${getVolume6Lock(bible)}

${buildEffectiveProseInstruction(bible)}

<INSPECTOR_FEEDBACK>
${inspectorFeedback}
</INSPECTOR_FEEDBACK>

<CURRENT_LEDGER>
${JSON.stringify(ledger, null, 2)}
</CURRENT_LEDGER>
${softStateContext}

<CANON_ANCHORS>
- ACTIVE LOCATIONS: ${activeLocations.length > 0 ? activeLocations.join(', ') : "Unknown"}
- ACTIVE CHARACTERS: ${activeCharacters.length > 0 ? activeCharacters.join(', ') : "Unknown"}
- AVAILABLE CAST: ${Object.values(ledger.characters || {}).map((c: any) => c.name).join(', ')}
- UNRESOLVED PLOT POINTS: ${formatCanonEntriesForPrompt(ledger.plot_points, { sourceField: 'ledger.plot_points' })}
- RESOLVED PLOT POINTS (DO NOT REPEAT): ${formatCanonEntriesForPrompt(ledger.resolved_plot_points, { sourceField: 'ledger.resolved_plot_points' })}
</CANON_ANCHORS>

STRICT RULE: The characters are currently at the Active Locations. They ALREADY KNOW the Resolved Plot Points. Do NOT write them discovering, theorizing, or being shocked by these facts. Treat these facts as mundane background knowledge.

<PREVIOUS_CHAPTER_SUMMARY>
${previousContext}
</PREVIOUS_CHAPTER_SUMMARY>

<CHAPTER_PLAN>
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}
</CHAPTER_PLAN>

<FAILED_DRAFT>
${failedDraft}
</FAILED_DRAFT>

Your task is to perform a TOTAL STRUCTURAL REWRITE of the <FAILED_DRAFT>.
You must:
1. RUTHLESSLY DELETE any paragraphs, scenes, or dialogue that violate the Inspector's feedback (e.g., teleporting characters, invented abilities, bad cutaways, omniscient knowledge).
2. IF THE SCRIBE SKIPPED A CRITICAL ON-SCREEN EVENT (like a kidnapping or battle), you MUST write that event happening in real-time. Do NOT use exposition or "video calls" to summarize it. Show it happening.
3. Rewrite the narrative to STRICTLY adhere to the Current Ledger. If a character isn't at the location, remove them. If an event already happened, do not repeat it.
4. EXECUTE THE TYPED CHAPTER PLAN, EXCEPT WHEN IT CONFLICTS WITH THE INSPECTOR'S FEEDBACK. IF THE INSPECTOR SAYS THE PLAN IS WRONG, THE INSPECTOR WINS. The plan contains typed beats. You must handle them accordingly:
   - [EXACT FACTS]: Must be executed literally, exactly as written. No deviation. Do not invent unlisted mechanics or tools.
   - [REQUIRED OUTCOMES]: The end result must happen, but you can invent the staging/action to get there.
   - [OPTIONAL STAGING]: Suggestions for action/dialogue. You can use them or invent your own if it fits better.
   - [EMOTIONAL PAYOFF ONLY]: Focus on the internal/emotional realization. The physical action doesn't matter as much.
   - [FORBIDDEN REPEAT]: CRITICAL! Do NOT write this happening. It is a trap. Do not summarize it. Do not mention it. It already happened.
5. If the <FAILED_DRAFT> is fundamentally broken, discard its structure entirely and write a completely new chapter from scratch that satisfies the plan and ledger, bridging logically from the <PREVIOUS_CHAPTER_SUMMARY>.
6. ONLY USE ACTIVE CHARACTERS IN ACTIVE LOCATIONS. Do not cut away to other locations unless explicitly required by the Chapter Plan.

STYLE GUIDE: ${bible.style_guide}
${HUMAN_PROSE_CONSTRAINT}
LENGTH: ${bible.chapterLength}

Return a JSON object:
{
  "chapter": {
    "title": "Chapter Title",
    "content": "The full, rewritten text of the chapter. Ensure it is detailed, immersive, and meets the length requirement.",
    "summary": "A brief summary of the events in this rewritten chapter."
  }
}`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.DIRECTOR_REWRITE.model,
    contents: prompt,
    config: {
      temperature: 0.6, // Increased slightly to allow for full structural rewrites
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.DIRECTOR_REWRITE.thinkingLevel },
      maxOutputTokens: 16384,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          chapter: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              content: { type: Type.STRING },
              summary: { type: Type.STRING }
            },
            required: ["title", "content", "summary"]
          }
        },
        required: ["chapter"]
      }
    }
  });

  let json = extractAndParseJSON(response.text || "");
  if (json.chapter) json = json.chapter;
  if (json.draft) json = json.draft;
  
  if (!json.content) {
    json.content = "Error: Model failed to generate content.";
  }

  const cleanedContent = await finalizeProseOutput(json.content, bible, 'director_rewrite');
  
  return { id: plan.chapterNumber, ...json, content: cleanedContent };
};

export const publishChapter = async (
  chapter: Chapter,
  bible: StoryBible,
  ledger: Ledger,
  plan?: ChapterPlan
): Promise<PublicationEditorResult> => {
  const planContext = plan ? `TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}` : "No specific plan provided.";

  const prompt = `
    PUBLICATION REVIEW REQUEST
    
    CHAPTER DRAFT:
    ${chapter.content}
    
    CHAPTER PLAN:
    ${planContext}
    
    STORY BIBLE (RAILS):
    TITLE: ${bible.title}
    PREMISE: ${JSON.stringify(bible.premise)}
    STYLE GUIDE: ${bible.style_guide}
    ${HUMAN_PROSE_CONSTRAINT}
    
    CANON STATE (LEDGER):
    ${JSON.stringify(ledger)}
    
    SERIES CONSTRAINTS:
    MODE: ${bible.mode}
    CHAPTERS: ${bible.targetChapterCount}
    LENGTH: ${bible.chapterLength}
    ${getVolume6Lock(bible)}
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.PUBLICATION_EDITOR.model,
    contents: prompt,
    config: {
      systemInstruction: appendForbiddenProseInstruction(SYSTEM_INSTRUCTIONS.PUBLICATION_EDITOR, bible),
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.PUBLICATION_EDITOR.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          patched_scenes: {
            type: Type.ARRAY,
            description: "The patched scenes. Maintain the length of the original scenes. Do not summarize or shorten them.",
            items: { type: Type.STRING }
          },
          change_log: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                issue: { type: Type.STRING },
                fix: { type: Type.STRING },
                location: { type: Type.STRING }
              }
            }
          },
          lore_compliance: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                rail: { type: Type.STRING },
                status: { type: Type.STRING },
                note: { type: Type.STRING }
              }
            }
          },
          architect_queries: {
            type: Type.ARRAY,
            items: { type: Type.STRING }
          }
        },
        required: ["patched_scenes", "change_log", "lore_compliance"]
      }
    },
  }, 240000);

  const res = extractAndParseJSON(response.text || "{}");
  
  if (res.patched_scenes && Array.isArray(res.patched_scenes)) {
    res.patched_content = res.patched_scenes.join('\n\n***\n\n');
    delete res.patched_scenes;
  }
  
  res.patched_content = await finalizeProseOutput(res.patched_content, bible, 'publication_editor');
  res.change_log = Array.isArray(res.change_log) ? res.change_log : [];
  res.lore_compliance = Array.isArray(res.lore_compliance) ? res.lore_compliance : [];
  
  return res;
};

export const repairEditChapter = async (
  chapter: Chapter,
  bible: StoryBible,
  ledger: Ledger,
  plan: any,
  originalText: string,
  minWords: number,
  targetPctRatio: number
): Promise<EditorResult> => {
  const instruction = appendForbiddenProseInstruction(
    SYSTEM_INSTRUCTIONS.EDITOR.replace('{{MODE}}', bible.mode) +
      `\nStyle Guide: ${bible.style_guide}\n${HUMAN_PROSE_CONSTRAINT}` +
      `\nACT AS A LENGTH PRESERVATION EDITOR. RESTORE WORD COUNT AND ENGAGEMENT. DO NOT CONDENSE OR SUMMARIZE.`,
    bible,
  );

  const planContext = plan ? `CHAPTER PLAN:
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map((b: any) => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}` : "None";

  const originalWordCount = originalText.split(/\s+/).filter(Boolean).length;
    const prompt = `
    You are the Senior Editor. A previous polish phase incorrectly compressed the draft's word count down to ${(chapter.content || '').split(/\s+/).filter(Boolean).length} words.
    ${getVolume6Lock(bible)}
    
    ORIGINAL INPUT DRAFT (${originalWordCount} words):
    ${originalText}
    
    COMPRESSED / POLISHED OUTPUT DRAFT:
    ${chapter.content}
    
    CURRENT LEDGER:
    ${JSON.stringify(ledger)}
    
    ${planContext}
    
    CRITICAL REPAIR INSTRUCTION:
    Restore the missing length without changing plot events, canon facts, chapter outcome, or chapter order. Keep all edits but expand sensory detail, action beats, internal reaction, dialogue subtext, and environmental pressure. Final output must be at least ${minWords} words and at least ${Math.round(targetPctRatio * 100)}% of the input draft length (minimum ${Math.round(originalWordCount * targetPctRatio)} words).
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.EDITOR.model,
    contents: prompt,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.EDITOR.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          feedback: { type: Type.STRING },
          polished_scenes: {
            type: Type.ARRAY,
            description: "The repaired, expanded polished scenes. Maintain length or expand it to hit the target words.",
            items: { type: Type.STRING }
          }
        },
        required: ["status", "feedback", "polished_scenes"]
      }
    },
  });
  const res = extractAndParseJSON(response.text || "");
  
  let polishedText = res.polished_text;
  if (res.polished_scenes && Array.isArray(res.polished_scenes)) {
    polishedText = res.polished_scenes.join('\n\n***\n\n');
  }

  const cleanedText = await finalizeProseOutput(polishedText || chapter.content, bible, 'editor_repair');

  return {
    status: res.status || 'APPROVED',
    feedback: res.feedback || 'Repaired length compression.',
    polished_text: cleanedText
  };
};

export const repairPublishChapter = async (
  chapter: Chapter,
  bible: StoryBible,
  ledger: Ledger,
  plan: any,
  originalText: string,
  minWords: number,
  targetPctRatio: number
): Promise<PublicationEditorResult> => {
  const planContext = plan ? `TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map((b: any) => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}` : "No specific plan provided.";

  const originalWordCount = originalText.split(/\s+/).filter(Boolean).length;
  const prompt = `
    PUBLICATION REVIEW REPAIR REQUEST
    
    A previous PublicationEditor check was too aggressive and shrank the polished draft from ${originalWordCount} words down to ${(chapter.content || '').split(/\s+/).filter(Boolean).length} words.
    ${getVolume6Lock(bible)}
    
    ORIGINAL EDITOR-APPROVED DRAFT (${originalWordCount} words):
    ${originalText}
    
    SHRUNK PUBLICATION DRAFT:
    ${chapter.content}
    
    CHAPTER PLAN:
    ${planContext}
    
    STORY BIBLE (RAILS):
    TITLE: ${bible.title}
    PREMISE: ${JSON.stringify(bible.premise)}
    STYLE GUIDE: ${bible.style_guide}
    ${HUMAN_PROSE_CONSTRAINT}
    
    CANON STATE (LEDGER):
    ${JSON.stringify(ledger)}
    
    CRITICAL REPAIR INSTRUCTION:
    PublicationEditor must be surgical only. It must fix punctuation, formatting, small clarity issues, and lore-safe wording.
    It MUST NOT condense, summarize, compress, or remove paragraphs.
    Restore the missing length and keep all original detailed paragraphs without changing plot events, canon facts, chapter outcome, or chapter order.
    The final output must be at least ${minWords} words and at least ${Math.round(targetPctRatio * 100)}% of the original Editor-approved draft (${Math.round(originalWordCount * targetPctRatio)} words).
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.PUBLICATION_EDITOR.model,
    contents: prompt,
    config: {
      systemInstruction: appendForbiddenProseInstruction(
        SYSTEM_INSTRUCTIONS.PUBLICATION_EDITOR + "\nSURGICAL COPYEDIT ONLY. DO NOT SUMMARIZE OR CONDENSE PROSE. PRESERVE AND RESTORE PROSE LENGTH.",
        bible,
      ),
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.PUBLICATION_EDITOR.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          patched_scenes: {
            type: Type.ARRAY,
            description: "The surgically-repaired scenes. Do not condense, summarize, compress, or remove paragraphs.",
            items: { type: Type.STRING }
          },
          change_log: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                issue: { type: Type.STRING },
                fix: { type: Type.STRING },
                location: { type: Type.STRING }
              }
            }
          },
          lore_compliance: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                rail: { type: Type.STRING },
                status: { type: Type.STRING },
                note: { type: Type.STRING }
              }
            }
          }
        },
        required: ["patched_scenes"]
      }
    }
  });

  const res = extractAndParseJSON(response.text || "{}");
  let patchedContent = res.patched_content;
  if (res.patched_scenes && Array.isArray(res.patched_scenes)) {
    patchedContent = res.patched_scenes.join('\n\n***\n\n');
  }

  res.change_log = Array.isArray(res.change_log) ? res.change_log : [];
  res.lore_compliance = Array.isArray(res.lore_compliance) ? res.lore_compliance : [];

  const cleanedContent = await finalizeProseOutput(patchedContent || chapter.content || '', bible, 'publication_editor_repair');

  return {
    patched_content: cleanedContent,
    change_log: res.change_log || [],
    lore_compliance: res.lore_compliance || [],
    architect_queries: res.architect_queries || []
  };
};

export type UpdateLedgerOptions = {
  physicalExtractionOnly?: boolean;
  archivistRawSnapshot?: Record<string, unknown>;
  inspectorPhysicalStateChanges?: import('../src/types').InspectorPhysicalStateChange[];
};

export { runPhysicalArchivistCorrection } from '../src/utils/physicalContinuityCorrection.service';

async function runPhysicalArchivistCorrectionViaTransport(
  chapter: Chapter,
  currentLedger: Ledger,
  mode: StoryMode,
  bible: StoryBible | undefined,
  validation: PhysicalContinuityExtractionValidation,
  rawData: Record<string, unknown>,
  logicalCallId: string,
): Promise<Record<string, unknown>> {
  return runPhysicalArchivistCorrection(
    {
      chapter,
      mode,
      bible,
      validation,
      rawData,
      existingLedger: currentLedger,
    },
    async (request) => {
      recordPhysicalCorrectionTransportAttempt();
      const startedAt = Date.now();
      try {
        const response = await generateWithRetry({
          model: WORKER_CONFIG.ARCHIVIST.model,
          contents: String(request.contents || ''),
          config: {
            systemInstruction: String(request.correctionInstruction || ''),
            thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel },
            responseMimeType: 'application/json',
            responseSchema: buildPhysicalCorrectionResponseSchema(),
          },
        }, Number(request.timeoutMs) || 240000, { logicalCallId, operation: 'PHYSICAL_EXTRACTION_CORRECTION' });
        const durationMs = Date.now() - startedAt;
        console.log(`[PHYSICAL_EXTRACTION_CORRECTION] logicalCallId=${logicalCallId} completed in ${durationMs}ms`);
        return { text: response.text || '{}' };
      } catch (error: any) {
        const durationMs = Date.now() - startedAt;
        console.warn(
          `PHYSICAL_EXTRACTION_CORRECTION logicalCallId=${logicalCallId} failed after ${durationMs}ms (${error?.message || 'unknown'}).`,
        );
        throw error;
      }
    },
  );
}

async function buildLedgerFromArchivistRaw(
  safeLedger: Ledger,
  rawData: Record<string, unknown>,
  chapter: Chapter,
  bible?: StoryBible,
  options?: UpdateLedgerOptions,
): Promise<Ledger> {
  const chapterNumber = typeof chapter.id === 'number' ? chapter.id : Number(chapter.chapterNumber) || 1;

  const physicalResult = await finalizePhysicalContinuityLedger({
    chapterContent: chapter.content || '',
    chapterNumber,
    existingLedger: safeLedger,
    rawData,
    projectId: bible?.canonId,
    inspectorPhysicalStateChanges: options?.inspectorPhysicalStateChanges,
    applyCorrection: ({ validation, rawData: currentRaw, logicalCallId }) =>
      runPhysicalArchivistCorrectionViaTransport(
        chapter,
        safeLedger,
        bible?.mode || 'NOVEL',
        bible,
        validation,
        currentRaw,
        logicalCallId,
      ),
  });

  const obligationMerged = markObligationsResolvedByChapter(
    {
      ...safeLedger,
      continuity_objects: physicalResult.ledger.continuity_objects,
      physical_continuity_obligations: physicalResult.ledger.physical_continuity_obligations,
    },
    chapter.content || '',
    chapterNumber,
  );

  const latestStateDelta = deriveLatestStateDelta({
    priorLedger: safeLedger,
    mergedLedger: obligationMerged,
    chapterNumber,
    chapterRevisionId: chapter.storagePath || String(chapter.id),
    archivistDelta: rawData.latestStateDelta,
  });

  return mergeAuthoritativeLedger({
    priorLedger: safeLedger,
    rawData,
    bible,
    physicalMerged: {
      continuity_objects: obligationMerged.continuity_objects,
      physical_continuity_obligations: obligationMerged.physical_continuity_obligations,
    },
    latestStateDelta,
  });
}

export { PhysicalContinuityExtractionError, PHYSICAL_EXTRACTION_CORRECTION_MAX };

export const updateLedger = async (
  chapter: Chapter,
  currentLedger: Ledger,
  mode: StoryMode,
  bible?: StoryBible,
  options?: UpdateLedgerOptions,
): Promise<Ledger> => {
    const safeLedger = getFilteredLedger(currentLedger, bible?.canonId || 'fallback', bible?.canonId || 'fallback');
    const chapterNumber = typeof chapter.id === 'number' ? chapter.id : Number(chapter.chapterNumber) || 1;

    if (options?.physicalExtractionOnly && options.archivistRawSnapshot) {
      console.log('[PHYSICAL_EXTRACTION_RESUME] Resuming physical continuity extraction from checkpoint snapshot.');
      return await buildLedgerFromArchivistRaw(safeLedger, options.archivistRawSnapshot, chapter, bible, options);
    }

  const eraStateStr = formatEraState(bible?.eraState);
  const physicalCandidates = prepareChapterPhysicalCandidates({
    prose: chapter.content || '',
    chapterNumber,
    inspectorChanges: options?.inspectorPhysicalStateChanges,
  });
  const instruction =
    SYSTEM_INSTRUCTIONS.ARCHIVIST +
    `\nAnalyze this ${mode} chapter and update the factual ledger. Move any completed plot_points into resolved_plot_points.\n\n${ARCHIVIST_PHYSICAL_MANIFEST_INSTRUCTION}`;
  
  // 1. Audit payload logging:
  const chapterTextCharCount = chapter.content?.length || 0;
  const ledgerCharCount = JSON.stringify(currentLedger).length;
  const manifestCharCount = JSON.stringify(physicalCandidates.manifest).length;
  const blueprintCharCount = 0; // Archivist does not receive full blueprint
  const contextCharCount = 0;
  const totalPromptCharCount = instruction.length + chapterTextCharCount + ledgerCharCount + manifestCharCount;

  console.log("[ARCHIVIST PAYLOAD AUDIT]", {
    chapterTextCharCount,
    ledgerCharCount,
    manifestCharCount,
    physicalCandidateCount: physicalCandidates.manifest.length,
    requiredStructuredCandidateCount: physicalCandidates.manifest.filter((entry) => entry.requiresStructuredObject).length,
    blueprintCharCount,
    contextCharCount,
    totalPromptCharCount
  });

  const timeoutMs = 240000;
  let lastError: any = null;
  let retryOccurred = false;

  for (let attempt = 1; attempt <= 3; attempt++) {
    const startedAt = new Date().toISOString();
    const startMs = Date.now();
    try {
      if (attempt > 1) {
        retryOccurred = true;
      }
      
      // 6. Optimize Archivist input: only pass structured essential segments
      const compactLedger = {
        characters: safeLedger.characters || {},
        locations: safeLedger.locations || {},
        inventory: safeLedger.inventory || [],
        plot_points: safeLedger.plot_points || [],
        resolved_plot_points: safeLedger.resolved_plot_points || [],
        momentum: safeLedger.momentum || "",
        arc_stage: safeLedger.arc_stage || "",
        continuity_objects: safeLedger.continuity_objects || [],
        physical_continuity_obligations: safeLedger.physical_continuity_obligations || [],
      };

      const contents = `CHAPTER NUMBER: ${chapter.chapterNumber}\nCHAPTER TITLE: ${chapter.title || 'Untitled'}\n\nPHYSICAL CANDIDATE MANIFEST:\n${JSON.stringify(physicalCandidates.manifest)}\n\nCHAPTER CONTENT:\n${chapter.content}\n\nCURRENT COMPACT LEDGER:\n${JSON.stringify(compactLedger)}`;

      const response = await generateWithRetry({
        model: WORKER_CONFIG.ARCHIVIST.model,
        contents,
        config: {
          systemInstruction: instruction,
          thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel },
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              characters: { type: Type.OBJECT },
              locations: { type: Type.OBJECT },
              inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
              plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
              resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
              momentum: { type: Type.STRING },
              arc_stage: { type: Type.STRING },
              ...buildArchivistInitialPhysicalArrayProperties(),
            },
            required: [...ARCHIVIST_INITIAL_LEDGER_REQUIRED_FIELDS]
          }
        },
      }, timeoutMs);

      const completedAt = new Date().toISOString();
      const durationMs = Date.now() - startMs;
      const reqId = lastRequestId || 'N/A';

      console.log(`[ARCHIVIST_INITIAL] Attempt ${attempt} completed in ${durationMs}ms | Request ID: ${reqId} | Retry Occurred: ${retryOccurred}`);

      const rawData = normalizeArchivistPhysicalResponse(extractAndParseJSON(response.text || "{}"));
      
      return await buildLedgerFromArchivistRaw(safeLedger, rawData, chapter, bible, options);
    } catch (error: any) {
      lastError = error;
      const durationMs = Date.now() - startMs;
      if (error instanceof PhysicalContinuityExtractionError || error?.name === 'PhysicalContinuityExtractionError') {
        console.warn(
          `PHYSICAL_CONTINUITY_EXTRACTION blocked after ${durationMs}ms on ARCHIVIST_INITIAL attempt ${attempt}. Not retrying Archivist.`,
        );
        throw error;
      }
      const label = error?.message?.toLowerCase?.().includes('timed out') ? 'timed out' : 'failed';
      console.warn(`ARCHIVIST_INITIAL attempt ${attempt} ${label} after ${durationMs}ms. Retrying...`);
    }
  }
  throw lastError;
};

export const updateLedgerFromNote = async (note: string, currentLedger: Ledger): Promise<Ledger> => {
  const instruction = SYSTEM_INSTRUCTIONS.ARCHIVIST + `\nAnalyze this Director's Note and update the factual ledger to reflect these changes in the story state. Ensure continuity is maintained while incorporating the new direction.`;
  const response = await generateWithRetry({
    model: WORKER_CONFIG.ARCHIVIST.model,
    contents: `DIRECTOR'S NOTE:\n${note}\n\nCURRENT LEDGER:\n${JSON.stringify(currentLedger)}`,
    config: {
      systemInstruction: instruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.ARCHIVIST.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          characters: { type: Type.OBJECT },
          locations: { type: Type.OBJECT },
          inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
          plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
          momentum: { type: Type.STRING },
          arc_stage: { type: Type.STRING }
        },
        required: ["characters", "locations", "inventory", "plot_points", "momentum", "arc_stage"]
      }
    },
  });
  const rawDataNote = extractAndParseJSON(response.text || "{}");
  return mergeAuthoritativeLedger({
    priorLedger: currentLedger,
    rawData: rawDataNote,
    physicalMerged: {
      continuity_objects: currentLedger.continuity_objects,
      physical_continuity_obligations: currentLedger.physical_continuity_obligations,
    },
    latestStateDelta: currentLedger.latestStateDelta,
  });
};

export const validateChapter = async (
  chapter: Chapter, 
  ledger: Ledger, 
  bible: StoryBible,
  directorsNote?: string, 
  retryCount: number = 0,
  plan?: ChapterPlan,
  authoritativeContinuitySnapshot?: AuthoritativeContinuitySnapshot,
): Promise<ValidationResult> => {
  const proseControls = getProseControls(bible);
  const chapterNum = typeof chapter.id === 'number' ? chapter.id : 1;
  const momentumEval = evaluateChapterMomentum(
    chapter.content || '',
    chapterNum,
    plan,
    proseControls,
    bible,
    ledger,
  );

  const safeLedger = getFilteredLedger(ledger, bible.canonId || 'fallback', bible.canonId || 'fallback');
  const canonKnowledge = buildChapterCanonKnowledge({
    ledger: safeLedger,
    storyBible: bible,
    plan,
    chapterNumber: chapterNum,
  });
  const canonKnowledgeContext = buildInspectorCanonKnowledgeBlock(canonKnowledge);
  const suspectedRevelations = assessSuspectedRepeatedRevelations(chapter.content || '', canonKnowledge);
  const suspectedRevelationContext = buildSuspectedRevelationInspectorContext(suspectedRevelations);
  const suspectedPhysicalContinuity = assessSuspectedPhysicalContinuity(chapter.content || '', safeLedger, chapterNum);
  const physicalContinuityContext = buildPhysicalContinuityInspectorContext(suspectedPhysicalContinuity);
  const physicalLedgerContext = authoritativeContinuitySnapshot
    ? ''
    : buildPhysicalContinuityContextSections(safeLedger, plan, chapterNum).combined;

  const systemInstruction = SYSTEM_INSTRUCTIONS.INSPECTOR +
    `\n\nSTYLE GUIDE: ${bible.style_guide}\n\n` +
    "STRICT HIERARCHY OF TRUTH:\n1. MASTER LEDGER (Absolute Canon)\n2. DIRECTOR'S NOTE (Immediate Priority)\n3. CHAPTER PLAN (Guidance only — AI-generated beats are flexible)\n\n" +
    "CRITICAL RULE: The Chapter Plan is AI-generated guidance. Do NOT treat AI-generated exact measurements, spacing, or geometry as locked canon.\n" +
    (directorsNote ? "\nSpecial Task: Ensure the Director's Note was followed correctly." : "") +
    getVolume6Lock(bible) +
    getVolume6InspectorCheck(bible) +
    `\n\n${canonKnowledgeContext}` +
    (suspectedRevelationContext ? `\n\n${suspectedRevelationContext}` : '') +
    (physicalContinuityContext ? `\n\n${physicalContinuityContext}` : '') +
    (physicalLedgerContext ? `\n\n${physicalLedgerContext}` : '') +
    `\n\n${buildInspectorMomentumAddendum(proseControls, chapterNum)}` +
    `\n\n${buildProseControlPrompt(proseControls)}`;

  const atomicEvents = plan ? extractAtomicChapterEvents(plan) : [];
  const atomicEventContext = buildInspectorAtomicEventContext(atomicEvents);

  let summaryWarningContext = '';
  if (plan && (chapter.content || '').includes(FOUR_BEAT_SEPARATOR)) {
    const contract = buildFourBeatChapterContract({
      plan,
      chapterNumber: chapterNum,
      chapterLength: 'STANDARD',
    });
    const sections = (chapter.content || '').split(FOUR_BEAT_SEPARATOR);
    const beats = contract.beats.map((contractBeat) =>
      toGeneratedChapterBeat({
        beatId: contractBeat.beatId,
        sequence: contractBeat.sequence,
        content: sections[contractBeat.sequence - 1] || '',
      }),
    );
    const generated = assembleFourBeatChapter({
      chapterId: chapterNum,
      title: chapter.title || plan.title,
      summary: plan.summary,
      beats,
    });
    summaryWarningContext = buildInspectorSummaryWarningContext(generated, contract);
  }

  const planContext = plan ? `CHAPTER PLAN (guidance only — beats are flexible):
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}

${atomicEventContext}
${summaryWarningContext ? `\n${summaryWarningContext}` : ''}
INSPECTOR EVENT VALIDATION:
- Validate atomic event completion by eventId.
- Report missing event IDs explicitly if any primary beat fails to dramatize its assigned event.
- Do NOT require verbatim roadmap paragraph text inside a single beat.
INSPECTOR BEAT REPAIR CONTRACT:
- Return verdict PASS when the chapter may continue without beat repair.
- Return verdict REPAIR_REQUIRED only when one or more beats need targeted rewrite; include beatRepairRequests with authoritative beat IDs beat-1 through beat-4.
- Return verdict REPAIR_REQUIRED with reasonCodes ["REPEATED_REVELATION"] when you confirm POV rediscovery of accepted canon; include the affected beatId.
- Return verdict REPAIR_REQUIRED with reasonCodes ["UNRESOLVED_PHYSICAL_CONTINUITY"] or ["MISSING_OBJECT_CARRYOVER"] when you confirm unresolved physical-object carryover; include objectId, previous state, and the affected beatId.
- Return physicalStateChanges for plot-relevant physical introductions, disablements, transfers, destruction, abandonment, or carried-forward objects. Use confidence HIGH only when continuity-relevant and unresolved at chapter end. You supply candidate evidence only; the Archivist remains ledger authority.
- Return verdict BLOCKED for chapter-wide continuity failures that require full rewrite (not for repairable repeated revelation framing).
- Pre-Inspector summary warnings alone must NOT force REPAIR_REQUIRED.
- Each beatRepairRequest must include beatId, reasonCodes, and explanation.` : "";

  const momentumContext = momentumEval.audit.warnings?.length || momentumEval.shouldBlock
    ? `\nDETERMINISTIC MOMENTUM PRECHECK:
Dimensions: continuity=${momentumEval.dimensions.continuity}, momentum=${momentumEval.dimensions.momentum}, repetition=${momentumEval.dimensions.repetition}, overall=${momentumEval.dimensions.overall}
${momentumEval.shouldBlockMomentum ? `BLOCKING MOMENTUM: ${momentumEval.reason}` : 'Momentum precheck: no hard momentum block.'}
${momentumEval.shouldBlockRepetition ? `BLOCKING REPETITION: ${momentumEval.correction.repetitionEvidence?.map(e => e.reason + ':' + e.term).join(', ')}` : momentumEval.dimensions.repetition === 'WARNING' ? 'Repetition warnings only — semantic judgment may override.' : 'No blocking repetition.'}
Audit: disruption at ${momentumEval.audit.firstDisruptionPositionPercent}%, routine ${momentumEval.audit.routineProcedurePercent}%, state changes ${momentumEval.audit.meaningfulStateChanges}.
Repetition findings: ${(momentumEval.audit.repetitionFindings || []).filter(f => f.severity !== 'INFO').map(f => `${f.severity}:${f.term}`).join(', ') || 'none'}.
Procedure sequences: ${momentumEval.audit.lowValueProcedureSequences.slice(0, 2).join(' | ') || 'none'}.`
    : '';

  const ledgerContext = authoritativeContinuitySnapshot
    ? `AUTHORITATIVE CONTINUITY SNAPSHOT (IDENTICAL FACTS FOR ALL AGENTS):\n${authoritativeContinuitySnapshot.promptText}`
    : `Ledger: ${JSON.stringify(safeLedger)}`;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.INSPECTOR.model,
    contents: `INSPECT CONTINUITY AND NARRATIVE MOTION:\nChapter: ${chapter.content}\n${ledgerContext}\n${planContext}\n${momentumContext}\n${directorsNote ? `DIRECTOR'S NOTE TO VERIFY: ${directorsNote}` : ""}`,
    config: {
      systemInstruction,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.INSPECTOR.thinkingLevel },
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          verdict: { type: Type.STRING },
          reason: { type: Type.STRING },
          correction_order: { type: Type.STRING },
          beatRepairRequests: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                beatId: { type: Type.STRING },
                reasonCodes: { type: Type.ARRAY, items: { type: Type.STRING } },
                explanation: { type: Type.STRING },
              },
              required: ['beatId', 'reasonCodes', 'explanation'],
            },
          },
          physicalStateChanges: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                candidateId: { type: Type.STRING },
                canonicalName: { type: Type.STRING },
                objectType: { type: Type.STRING },
                changeType: { type: Type.STRING },
                ownerOrHolder: { type: Type.STRING },
                location: { type: Type.STRING },
                unresolvedAtChapterEnd: { type: Type.BOOLEAN },
                beatId: { type: Type.STRING },
                confidence: { type: Type.STRING },
              },
            },
          },
        },
        required: ['status', 'reason', 'correction_order', 'verdict'],
      }
    },
  });

  let res: Record<string, unknown>;
  try {
    res = extractAndParseJSON(response.text || "");
  } catch (parseError: any) {
    const parseErr = Object.assign(new Error(parseError?.message || 'Inspector response parse failed'), {
      name: 'InspectorResponseParseError',
    });
    logInspectorResponseShape(
      analyzeInspectorResponseShape(null, false, 'INSPECTOR_RESPONSE_PARSE_FAILURE'),
    );
    throw parseErr;
  }

  logInspectorResponseShape(analyzeInspectorResponseShape(res, true));
  const physicalStateChanges = normalizeInspectorPhysicalStateChanges(res.physicalStateChanges);
  const rawVerdict = String(res.verdict || res.status || 'PASS').toUpperCase();
  const { accepted: beatRepairRequests, rejected: rejectedBeatRepairIds } =
    rawVerdict === 'REPAIR_REQUIRED'
      ? normalizeInspectorBeatRepairRequests(res.beatRepairRequests)
      : { accepted: [], rejected: [] };

  const buildResult = (
    verdict: import('../src/types').InspectorVerdict,
    overrides: Partial<ValidationResult> = {},
  ): ValidationResult =>
    mapInspectorResponseToValidationResult({
      verdict,
      reason: overrides.reason ?? res.reason,
      correction_order: overrides.correction_order ?? res.correction_order,
      beatRepairRequests: overrides.beatRepairRequests ?? (verdict === 'REPAIR_REQUIRED' ? beatRepairRequests : undefined),
      rejectedBeatRepairIds: rejectedBeatRepairIds.length > 0 ? rejectedBeatRepairIds : undefined,
      momentum_audit: overrides.momentum_audit,
      momentum_correction: overrides.momentum_correction,
      physicalStateChanges,
    });

  if (momentumEval.shouldBlockMomentum) {
    const momentumOrder = formatMomentumCorrectionOrder(momentumEval.correction);
    const combinedReason = [momentumEval.reason, res.reason].filter(Boolean).join(' | ');
    const combinedOrder = res.status === 'FAIL' && res.correction_order
      ? `${momentumOrder}\n\n${res.correction_order}`
      : momentumOrder;
    return buildResult('BLOCKED', {
      reason: combinedReason || 'Narrative momentum padding detected.',
      correction_order: combinedOrder,
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.correction,
    });
  }

  if (momentumEval.shouldBlockRepetition) {
    const momentumOrder = formatMomentumCorrectionOrder(momentumEval.correction);
    return buildResult('BLOCKED', {
      reason: momentumEval.reason || 'Blocking descriptive repetition detected.',
      correction_order: momentumOrder,
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.correction,
    });
  }

  if (rawVerdict === 'REPAIR_REQUIRED' && beatRepairRequests.length > 0) {
    return buildResult('REPAIR_REQUIRED', {
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.shouldBlock ? momentumEval.correction : undefined,
    });
  }

  const confirmedRepeatedRepair = resolveConfirmedRepeatedRevelationRepair({
    suspectedFindings: suspectedRevelations,
    beatRepairRequests,
    reason: res.reason,
    correction_order: res.correction_order,
  });
  if (confirmedRepeatedRepair) {
    return buildResult('REPAIR_REQUIRED', {
      reason: res.reason,
      correction_order: res.correction_order,
      beatRepairRequests: [confirmedRepeatedRepair],
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.shouldBlock ? momentumEval.correction : undefined,
    });
  }

  const confirmedPhysicalRepair = resolveConfirmedPhysicalContinuityRepair({
    suspectedFindings: suspectedPhysicalContinuity,
    beatRepairRequests,
    reason: res.reason,
    correction_order: res.correction_order,
  });
  if (confirmedPhysicalRepair) {
    return buildResult('REPAIR_REQUIRED', {
      reason: res.reason || confirmedPhysicalRepair.explanation,
      correction_order: res.correction_order || confirmedPhysicalRepair.explanation,
      beatRepairRequests: [confirmedPhysicalRepair],
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.shouldBlock ? momentumEval.correction : undefined,
    });
  }

  if (rawVerdict === 'REPAIR_REQUIRED' && beatRepairRequests.length === 0) {
    return buildResult('BLOCKED', {
      reason: res.reason || 'Inspector requested repair but provided no valid authoritative beat IDs.',
      correction_order: res.correction_order,
      rejectedBeatRepairIds: rejectedBeatRepairIds.length > 0 ? rejectedBeatRepairIds : undefined,
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.shouldBlock ? momentumEval.correction : undefined,
    });
  }

  if (res.status === 'PASS' && momentumEval.dimensions.repetition === 'WARNING') {
    return buildResult('PASS', {
      reason: [res.reason, `Repetition warning (non-blocking): ${momentumEval.audit.repetitionSeverity}`].filter(Boolean).join(' | '),
      correction_order: res.correction_order,
      momentum_audit: momentumEval.audit,
    });
  }

  if (rawVerdict === 'PASS' || res.status === 'PASS') {
    return promoteRepairableInspectorVerdict(
      buildResult('PASS', {
      reason: res.reason || 'No specific issues detected.',
      correction_order: res.correction_order,
      momentum_audit: momentumEval.audit,
      momentum_correction: momentumEval.shouldBlock ? momentumEval.correction : undefined,
    }),
      chapter,
      plan,
    );
  }

  return promoteRepairableInspectorVerdict(
    buildResult('BLOCKED', {
    reason: res.reason || 'Continuity validation failed.',
    correction_order: res.correction_order,
    momentum_audit: momentumEval.audit,
    momentum_correction: momentumEval.shouldBlock ? momentumEval.correction : undefined,
  }),
    chapter,
    plan,
  );
};

export const rewriteChapter = async (
  chapter: Chapter,
  ledger: Ledger,
  instructions: string,
  bible: StoryBible,
  previousContext?: string,
  plan?: ChapterPlan
): Promise<any> => {
  const activeLocations = Object.values(ledger.locations || {}).filter((l: any) => l.status?.includes('Active')).map((l: any) => `${l.name}: ${l.desc}`).join('\n');
  const activeCharacters = Object.values(ledger.characters || {}).filter((c: any) => c.status?.includes('Active')).map((c: any) => `${c.name}: ${c.desc}`).join('\n');

  const planContext = plan ? `CHAPTER PLAN:
TITLE: ${plan.title}
SUMMARY: ${plan.summary}
REQUIRED BEATS:
${(plan.requiredBeats || []).map(b => `- ${b}`).join('\n')}
CHARACTER FOCUS: ${(plan.characterFocus || []).join(', ')}
LOCATION FOCUS: ${(plan.locationFocus || []).join(', ')}
CONTINUITY NOTES: ${(plan.continuityNotes || []).join(', ')}
FORBIDDEN MOVES: ${(plan.forbiddenMoves || []).join(', ')}` : "None";

  const prompt = `
    ### INPUT STRUCTURE:
    ${getVolume6Lock(bible)}

    - [PAST SCENE SUMMARY]: ${previousContext || "None provided."}
    - [ACTIVE LORE]: 
      LOCATIONS:
      ${activeLocations || "None"}
      ACTIVE CHARACTERS:
      ${activeCharacters || "None"}
      AVAILABLE CAST:
      ${Object.values(ledger.characters || {}).map((c: any) => c.name).join(', ')}
    - [MANUSCRIPT DRAFT]: 
      ${chapter.content}
    - [CHAPTER PLAN]: 
      ${planContext}
    - [CHAPTER GOAL]: ${plan ? plan.summary : instructions}
    - [STYLE GUIDE]: ${bible.style_guide}
    ${HUMAN_PROSE_CONSTRAINT}
    
    REWRITE INSTRUCTIONS:
    "${instructions}"
  `;

  const response = await generateWithRetry({
    model: WORKER_CONFIG.DIRECTOR_REWRITE.model,
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.REWRITER,
      thinkingConfig: { thinkingLevel: WORKER_CONFIG.DIRECTOR_REWRITE.thinkingLevel },
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          failure_reason: { type: Type.STRING },
          scenes: {
            type: Type.ARRAY,
            items: { type: Type.STRING }
          },
          canon_delta: { type: Type.STRING },
          editor_notes: { type: Type.STRING }
        },
        required: ["status", "scenes", "canon_delta", "editor_notes"]
      }
    },
  });

  const json = extractAndParseJSON(response.text || "{}");
  
  if (json.scenes && Array.isArray(json.scenes)) {
    json.content = json.scenes.join('\n\n***\n\n');
    delete json.scenes;
  } else if (!json.content && json.status === "PASS") {
    json.content = "Error: Model failed to generate rewritten content.";
  }
  
  return json;
};

export const generateSpeech = async (text: string): Promise<string | null> => {
  // TTS requires the audio modality which is not supported by the JSON /api/generate
  // endpoint. In server-transport mode, TTS is gracefully disabled.
  // To restore TTS: add a dedicated /api/tts endpoint that streams the audio bytes.
  if (USE_SERVER_GEMINI) {
    console.warn('[gemini] generateSpeech: TTS is disabled in server-transport mode. Returning null.');
    return null;
  }
  // Dev path only (VITE_USE_SERVER_GEMINI=false)
  try {
    const { GoogleGenAI, Modality } = await import('@google/genai');
    const apiKey =
      (typeof process !== 'undefined' && process.env?.GEMINI_API_KEY) ||
      (import.meta as any).env?.VITE_GEMINI_API_KEY ||
      (import.meta as any).env?.VITE_API_KEY;
    if (!apiKey) return null;
    const ai = new GoogleGenAI({ apiKey });
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-preview-tts",
      contents: [{ parts: [{ text }] }],
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } },
      },
    });
    return response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data || null;
  } catch (error) {
    console.error("TTS Generation Error:", error);
    return null;
  }
};

export const reconcileManuscriptToCanon = async (
  manuscript: string,
  currentLedger: Ledger,
  currentBible: StoryBible
): Promise<{ updatedLedger: Ledger, updatedBible: StoryBible }> => {
  const prompt = `
    You are the NARRATIVE ARCHITECT and ARCHIVIST. 
    The author has provided the FINAL edited manuscript for the current volume.
    
    Your task is to analyze the entire manuscript and update the Story Memory (Ledger) and Story Blueprint (Bible) to strictly reflect what actually happened in the final text.
    Ignore any previous plans if they contradict the final manuscript.
    
    1. Update the Ledger: characters (alive/dead/status), relationships, locations, major events, inventory, momentum, arc_stage.
    2. Update the Plot Points: Move all resolved plot points to 'historical_events'. Retain unresolved plot points in 'plot_points'.
    3. Update the Story Bible: Ensure the ending direction and permanent change in the premise reflect the final chapter's ending state.
    
    Current Ledger:
    ${JSON.stringify(currentLedger)}
    
    Current Story Bible (Premise):
    ${JSON.stringify(currentBible.premise)}
    
    Here is the full manuscript:
    <manuscript>
    ${manuscript}
    </manuscript>
  `;

  const response = await generateWithRetry({
    model: GEMINI_MODEL_FLASH, // Using Flash to handle large context
    contents: prompt,
    config: {
      systemInstruction: SYSTEM_INSTRUCTIONS.ARCHIVIST + "\\nPerform a rigorous, fact-based reconciliation. Do not hallucinate. The manuscript is the ultimate source of truth.",
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          ledger: {
            type: Type.OBJECT,
            properties: {
              characters: { type: Type.OBJECT },
              locations: { type: Type.OBJECT },
              inventory: { type: Type.ARRAY, items: { type: Type.STRING } },
              plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
              resolved_plot_points: { type: Type.ARRAY, items: { type: Type.STRING } },
              historical_events: { type: Type.ARRAY, items: { type: Type.STRING } },
              momentum: { type: Type.STRING },
              arc_stage: { type: Type.STRING }
            },
            required: ["characters", "locations", "plot_points", "momentum", "arc_stage"]
          },
          biblePremise: {
            type: Type.OBJECT,
            properties: {
              surface: { type: Type.STRING },
              tension: { type: Type.STRING },
              change: { type: Type.STRING }
            },
            required: ["surface", "tension", "change"]
          }
        },
        required: ["ledger", "biblePremise"]
      }
    }
  });

  const rawData = extractAndParseJSON(response.text || "{}");
  
  const updatedLedger = {
    ...currentLedger,
    ...(rawData.ledger || {}),
    last_updated: new Date().toISOString()
  };
  
  const updatedBible = {
    ...currentBible,
    premise: rawData.biblePremise || currentBible.premise
  };

  return { updatedLedger, updatedBible };
};
